/**
 * icons.tsx
 *
 * Centralized icon components.
 * Each icon accepts `size` (number, px) and `color` (CSS color string).
 *
 * Usage:
 *   <GptIcon size={16} color="#13c2c2" />
 *   <MenuIcon size={24} color="#90A2AB" />
 *   <PlatformIcon platform="facebook" size={24} />
 */

import Icon from '@ant-design/icons';
import React, { CSSProperties, FC, useId } from 'react';

export interface IconProps {
  size?: number;
  color?: string;
  style?: CSSProperties;
}

export interface InteractiveIconProps extends IconProps {
  onClick?: React.MouseEventHandler<HTMLSpanElement>;
  className?: string;
}

// antd's built-in icon components (e.g. DeleteOutlined) auto-generate an
// `anticon-{name}` class; our custom Icon({ component }) wrapper does not,
// since it carries no icon-name metadata. This reproduces that convention
// so CSS and test selectors written against antd's naming keep working.
const anticonClass = (name: string, className?: string) =>
  [`anticon-${name}`, className].filter(Boolean).join(' ');

interface WeightedPathProps {
  d: string;
  color: string;
  weight: number;
  evenOdd?: boolean;
}

const WeightedPath: FC<WeightedPathProps> = ({ d, color, weight, evenOdd }) => {
  const maskId = useId();
  const fillRule = evenOdd ? 'evenodd' : undefined;

  if (weight >= 0) {
    return (
      <path
        d={d}
        fillRule={fillRule}
        clipRule={fillRule}
        fill={color}
        stroke={weight > 0 ? color : undefined}
        strokeWidth={weight || undefined}
        strokeLinejoin="round"
      />
    );
  }

  return (
    <>
      <mask id={maskId} maskUnits="userSpaceOnUse">
        <path d={d} fillRule={fillRule} fill="white" />
        <path d={d} fill="none" stroke="black" strokeWidth={-weight} strokeLinejoin="round" />
      </mask>
      <path d={d} fillRule={fillRule} clipRule={fillRule} fill={color} mask={`url(#${maskId})`} />
    </>
  );
};

interface WeightedIconSpec {
  d: string;
  weight: number;
  viewBox?: string;
  overflow?: boolean;
  evenOdd?: boolean;
  defaultSize?: number;
  defaultColor?: string;
}

const createWeightedIcon = ({
  d,
  weight,
  viewBox = '64 64 896 896',
  overflow = true,
  evenOdd,
  defaultSize = 24,
  defaultColor = '#90A2AB',
}: WeightedIconSpec): FC<IconProps> => {
  const svgByVariant = new Map<string, FC>();

  const svgFor = (size: number, color: string): FC => {
    const key = `${size}|${color}`;
    const cached = svgByVariant.get(key);
    if (cached) return cached;

    const Svg: FC = () => (
      <svg
        height={size}
        width={size}
        viewBox={viewBox}
        fill="none"
        overflow={overflow ? 'visible' : undefined}
        xmlns="http://www.w3.org/2000/svg"
      >
        <WeightedPath d={d} color={color} weight={weight} evenOdd={evenOdd} />
      </svg>
    );

    svgByVariant.set(key, Svg);
    return Svg;
  };

  return ({ size = defaultSize, color = defaultColor }) => <Icon component={svgFor(size, color)} />;
};

// AudioOutlined
export const AudioIcon: FC<IconProps> = createWeightedIcon({
  d: 'M842 454c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8 0 140.3-113.7 254-254 254S258 594.3 258 454c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8 0 168.7 126.6 307.9 290 327.6V884H326.7c-13.7 0-24.7 14.3-24.7 32v36c0 4.4 2.8 8 6.2 8h407.6c3.4 0 6.2-3.6 6.2-8v-36c0-17.7-11-32-24.7-32H548V782.1c165.3-18 294-158 294-328.1zM512 624c93.9 0 170-75.2 170-168V232c0-92.8-76.1-168-170-168s-170 75.2-170 168v224c0 92.8 76.1 168 170 168zm-94-392c0-50.6 41.9-92 94-92s94 41.4 94 92v224c0 50.6-41.9 92-94 92s-94-41.4-94-92V232z',
  weight: -8.6,
  overflow: false,
});

// ReadOutlined
export const ReadIcon: FC<IconProps> = createWeightedIcon({
  d: 'M928 161H699.2c-49.1 0-97.1 14.1-138.4 40.7L512 233l-48.8-31.3A255.2 255.2 0 00324.8 161H96c-17.7 0-32 14.3-32 32v568c0 17.7 14.3 32 32 32h228.8c49.1 0 97.1 14.1 138.4 40.7l44.4 28.6c1.3.8 2.8 1.3 4.3 1.3s3-.4 4.3-1.3l44.4-28.6C602 807.1 650.1 793 699.2 793H928c17.7 0 32-14.3 32-32V193c0-17.7-14.3-32-32-32zM324.8 721H136V233h188.8c35.4 0 69.8 10.1 99.5 29.2l48.8 31.3 6.9 4.5v462c-47.6-25.6-100.8-39-155.2-39zm563.2 0H699.2c-54.4 0-107.6 13.4-155.2 39V298l6.9-4.5 48.8-31.3c29.7-19.1 64.1-29.2 99.5-29.2H888v488zM396.9 361H211.1c-3.9 0-7.1 3.4-7.1 7.5v45c0 4.1 3.2 7.5 7.1 7.5h185.7c3.9 0 7.1-3.4 7.1-7.5v-45c.1-4.1-3.1-7.5-7-7.5zm223.1 7.5v45c0 4.1 3.2 7.5 7.1 7.5h185.7c3.9 0 7.1-3.4 7.1-7.5v-45c0-4.1-3.2-7.5-7.1-7.5H627.1c-3.9 0-7.1 3.4-7.1 7.5zM396.9 501H211.1c-3.9 0-7.1 3.4-7.1 7.5v45c0 4.1 3.2 7.5 7.1 7.5h185.7c3.9 0 7.1-3.4 7.1-7.5v-45c.1-4.1-3.1-7.5-7-7.5zm416 0H627.1c-3.9 0-7.1 3.4-7.1 7.5v45c0 4.1 3.2 7.5 7.1 7.5h185.7c3.9 0 7.1-3.4 7.1-7.5v-45c.1-4.1-3.1-7.5-7-7.5z',
  weight: 1.55,
});

// GlobalOutlined
export const GlobeIcon: FC<IconProps> = createWeightedIcon({
  d: 'M854.4 800.9c.2-.3.5-.6.7-.9C920.6 722.1 960 621.7 960 512s-39.4-210.1-104.8-288c-.2-.3-.5-.5-.7-.8-1.1-1.3-2.1-2.5-3.2-3.7-.4-.5-.8-.9-1.2-1.4l-4.1-4.7-.1-.1c-1.5-1.7-3.1-3.4-4.6-5.1l-.1-.1c-3.2-3.4-6.4-6.8-9.7-10.1l-.1-.1-4.8-4.8-.3-.3c-1.5-1.5-3-2.9-4.5-4.3-.5-.5-1-1-1.6-1.5-1-1-2-1.9-3-2.8-.3-.3-.7-.6-1-1C736.4 109.2 629.5 64 512 64s-224.4 45.2-304.3 119.2c-.3.3-.7.6-1 1-1 .9-2 1.9-3 2.9-.5.5-1 1-1.6 1.5-1.5 1.4-3 2.9-4.5 4.3l-.3.3-4.8 4.8-.1.1c-3.3 3.3-6.5 6.7-9.7 10.1l-.1.1c-1.6 1.7-3.1 3.4-4.6 5.1l-.1.1c-1.4 1.5-2.8 3.1-4.1 4.7-.4.5-.8.9-1.2 1.4-1.1 1.2-2.1 2.5-3.2 3.7-.2.3-.5.5-.7.8C103.4 301.9 64 402.3 64 512s39.4 210.1 104.8 288c.2.3.5.6.7.9l3.1 3.7c.4.5.8.9 1.2 1.4l4.1 4.7c0 .1.1.1.1.2 1.5 1.7 3 3.4 4.6 5l.1.1c3.2 3.4 6.4 6.8 9.6 10.1l.1.1c1.6 1.6 3.1 3.2 4.7 4.7l.3.3c3.3 3.3 6.7 6.5 10.1 9.6 80.1 74 187 119.2 304.5 119.2s224.4-45.2 304.3-119.2a300 300 0 0010-9.6l.3-.3c1.6-1.6 3.2-3.1 4.7-4.7l.1-.1c3.3-3.3 6.5-6.7 9.6-10.1l.1-.1c1.5-1.7 3.1-3.3 4.6-5 0-.1.1-.1.1-.2 1.4-1.5 2.8-3.1 4.1-4.7.4-.5.8-.9 1.2-1.4a99 99 0 003.3-3.7zm4.1-142.6c-13.8 32.6-32 62.8-54.2 90.2a444.07 444.07 0 00-81.5-55.9c11.6-46.9 18.8-98.4 20.7-152.6H887c-3 40.9-12.6 80.6-28.5 118.3zM887 484H743.5c-1.9-54.2-9.1-105.7-20.7-152.6 29.3-15.6 56.6-34.4 81.5-55.9A373.86 373.86 0 01887 484zM658.3 165.5c39.7 16.8 75.8 40 107.6 69.2a394.72 394.72 0 01-59.4 41.8c-15.7-45-35.8-84.1-59.2-115.4 3.7 1.4 7.4 2.9 11 4.4zm-90.6 700.6c-9.2 7.2-18.4 12.7-27.7 16.4V697a389.1 389.1 0 01115.7 26.2c-8.3 24.6-17.9 47.3-29 67.8-17.4 32.4-37.8 58.3-59 75.1zm59-633.1c11 20.6 20.7 43.3 29 67.8A389.1 389.1 0 01540 327V141.6c9.2 3.7 18.5 9.1 27.7 16.4 21.2 16.7 41.6 42.6 59 75zM540 640.9V540h147.5c-1.6 44.2-7.1 87.1-16.3 127.8l-.3 1.2A445.02 445.02 0 00540 640.9zm0-156.9V383.1c45.8-2.8 89.8-12.5 130.9-28.1l.3 1.2c9.2 40.7 14.7 83.5 16.3 127.8H540zm-56 56v100.9c-45.8 2.8-89.8 12.5-130.9 28.1l-.3-1.2c-9.2-40.7-14.7-83.5-16.3-127.8H484zm-147.5-56c1.6-44.2 7.1-87.1 16.3-127.8l.3-1.2c41.1 15.6 85 25.3 130.9 28.1V484H336.5zM484 697v185.4c-9.2-3.7-18.5-9.1-27.7-16.4-21.2-16.7-41.7-42.7-59.1-75.1-11-20.6-20.7-43.3-29-67.8 37.2-14.6 75.9-23.3 115.8-26.1zm0-370a389.1 389.1 0 01-115.7-26.2c8.3-24.6 17.9-47.3 29-67.8 17.4-32.4 37.8-58.4 59.1-75.1 9.2-7.2 18.4-12.7 27.7-16.4V327zM365.7 165.5c3.7-1.5 7.3-3 11-4.4-23.4 31.3-43.5 70.4-59.2 115.4-21-12-40.9-26-59.4-41.8 31.8-29.2 67.9-52.4 107.6-69.2zM165.5 365.7c13.8-32.6 32-62.8 54.2-90.2 24.9 21.5 52.2 40.3 81.5 55.9-11.6 46.9-18.8 98.4-20.7 152.6H137c3-40.9 12.6-80.6 28.5-118.3zM137 540h143.5c1.9 54.2 9.1 105.7 20.7 152.6a444.07 444.07 0 00-81.5 55.9A373.86 373.86 0 01137 540zm228.7 318.5c-39.7-16.8-75.8-40-107.6-69.2 18.5-15.8 38.4-29.7 59.4-41.8 15.7 45 35.8 84.1 59.2 115.4-3.7-1.4-7.4-2.9-11-4.4zm292.6 0c-3.7 1.5-7.3 3-11 4.4 23.4-31.3 43.5-70.4 59.2-115.4 21 12 40.9 26 59.4 41.8a373.81 373.81 0 01-107.6 69.2z',
  weight: 1.4,
});

// DiffOutlined
export const DiffIcon: FC<IconProps> = createWeightedIcon({
  d: 'M476 399.1c0-3.9-3.1-7.1-7-7.1h-42c-3.8 0-7 3.2-7 7.1V484h-84.5c-4.1 0-7.5 3.1-7.5 7v42c0 3.8 3.4 7 7.5 7H420v84.9c0 3.9 3.2 7.1 7 7.1h42c3.9 0 7-3.2 7-7.1V540h84.5c4.1 0 7.5-3.2 7.5-7v-42c0-3.9-3.4-7-7.5-7H476v-84.9zM560.5 704h-225c-4.1 0-7.5 3.2-7.5 7v42c0 3.8 3.4 7 7.5 7h225c4.1 0 7.5-3.2 7.5-7v-42c0-3.8-3.4-7-7.5-7zm-7.1-502.6c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v704c0 17.7 14.3 32 32 32h512c17.7 0 32-14.3 32-32V397.3c0-8.5-3.4-16.6-9.4-22.6L553.4 201.4zM664 888H232V264h282.2L664 413.8V888zm190.2-581.4L611.3 72.9c-6-5.7-13.9-8.9-22.2-8.9H296c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h277l219 210.6V824c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V329.6c0-8.7-3.5-17-9.8-23z',
  weight: 1.55,
});

// Shared <svg> wrapper for the common case: a fixed-size, no-fill viewport
// wrapped in an antd <Icon>. Centralizing it avoids repeating the xmlns/fill
// boilerplate (and its "visible" overflow value) across every icon below.
interface SimpleIconSvgProps {
  size: number;
  viewBox: string;
  overflow?: 'visible';
  children: React.ReactNode;
}

// Renders the same markup antd's generic <Icon component={...}> wrapper
// would, without defining a nested component per render (Sonar S6478) -
// the `component` prop would otherwise need a fresh closure on every
// SimpleIconSvg render to carry this call's size/viewBox/overflow/children.
const SimpleIconSvg: FC<SimpleIconSvgProps> = ({ size, viewBox, overflow, children }) => (
  <span className="anticon">
    <svg
      height={size}
      width={size}
      viewBox={viewBox}
      fill="none"
      overflow={overflow}
      xmlns="http://www.w3.org/2000/svg"
    >
      {children}
    </svg>
  </span>
);

interface StrokeIconSpec {
  paths: string[];
  viewBox?: string;
  defaultSize?: number;
  defaultColor?: string;
}

const createStrokeIcon = ({
  paths,
  viewBox = '0 0 24 24',
  defaultSize = 16,
  defaultColor = 'currentColor',
}: StrokeIconSpec): FC<IconProps> => {
  const svgByVariant = new Map<string, FC>();

  const svgFor = (size: number, color: string): FC => {
    const key = `${size}|${color}`;
    const cached = svgByVariant.get(key);
    if (cached) return cached;

    const Svg: FC = () => (
      <svg
        height={size}
        width={size}
        viewBox={viewBox}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
      >
        {paths.map((d) => (
          <path key={d} d={d} />
        ))}
      </svg>
    );

    svgByVariant.set(key, Svg);
    return Svg;
  };

  return ({ size = defaultSize, color = defaultColor, style }) => (
    <Icon style={style} component={svgFor(size, color)} />
  );
};

// ===========================================================================
// AI / SYSTEM ICONS
// ===========================================================================

export const GptIcon: FC<IconProps> = ({ size = 16, color = '#13c2c2', style }) => (
  <Icon
    style={style}
    component={() => (
      <svg
        fill={color}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        stroke={color}
        strokeWidth="0"
      >
        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
      </svg>
    )}
  />
);

// ===========================================================================
// NAVIGATION ICONS
// ===========================================================================

export const MenuIcon: FC<IconProps> = ({ size = 19, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 19 19" overflow="visible">
    <WeightedPath
      d="M2.625 0.105469H15.9902C17.379 0.105469 18.5097 1.23533 18.5098 2.625L18.5088 15.9902C18.5088 17.3791 17.3789 18.5098 15.9893 18.5098H2.625C1.23623 18.5098 0.105469 17.3799 0.105469 15.9902V2.625C0.105507 1.23618 1.23541 0.105469 2.625 0.105469ZM2.625 1.32617C1.9088 1.32617 1.32621 1.90879 1.32617 2.625V15.9912C1.32617 16.7075 1.90878 17.29 2.625 17.29H5.83398V1.32617H2.625ZM7.05371 17.29H15.9902C16.7065 17.29 17.2891 16.7075 17.2891 15.9912V2.625C17.289 1.90879 16.7064 1.32617 15.9902 1.32617H7.05371V17.29ZM3.10254 8.69824H4.05762C4.39478 8.69824 4.66797 8.9714 4.66797 9.30859C4.66773 9.64558 4.39464 9.91797 4.05762 9.91797H3.10254C2.76567 9.91779 2.49243 9.64547 2.49219 9.30859C2.49219 8.97151 2.76553 8.69842 3.10254 8.69824ZM3.10254 5.83398H4.05762C4.39478 5.83398 4.66797 6.10714 4.66797 6.44434C4.66784 6.78142 4.39471 7.05469 4.05762 7.05469H3.10254C2.7656 7.05451 2.49231 6.78131 2.49219 6.44434C2.49219 6.10725 2.76553 5.83416 3.10254 5.83398ZM3.10254 2.96973H4.05762C4.39478 2.96973 4.66797 3.24288 4.66797 3.58008C4.66796 3.91726 4.39478 4.19043 4.05762 4.19043H3.10254C2.76553 4.19025 2.4922 3.91715 2.49219 3.58008C2.49219 3.24299 2.76553 2.96991 3.10254 2.96973Z"
      color={color}
      weight={0.36}
    />
  </SimpleIconSvg>
);

export const SidebarToggleIcon: FC<IconProps> = ({ size = 20, color = '#0D316E' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 20">
    <path
      d="M4.15546 9.56424C4.39637 9.56424 4.59219 9.76004 4.59221 10.001L4.58337 10.0883C4.54268 10.287 4.36633 10.4367 4.15546 10.4367H3.09353C2.8528 10.4366 2.65704 10.2416 2.65679 10.001L2.66562 9.91265C2.70645 9.71405 2.88294 9.56434 3.09353 9.56424H4.15546Z"
      fill={color}
    />
    <path
      d="M4.15546 6.37648C4.39638 6.37648 4.59221 6.57227 4.59221 6.81323C4.59179 7.05375 4.3962 7.24801 4.15546 7.24801H3.09353C2.85289 7.2479 2.6572 7.05368 2.65679 6.81323C2.65679 6.57234 2.85272 6.3766 3.09353 6.37648H4.15546Z"
      fill={color}
    />
    <path
      d="M4.15546 3.18873C4.39629 3.18873 4.59207 3.38366 4.59221 3.6245C4.59204 3.86522 4.39636 4.06026 4.15546 4.06026H3.09353C2.85273 4.06015 2.65696 3.86515 2.65679 3.6245C2.65692 3.38373 2.8528 3.18885 3.09353 3.18873H4.15546Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.4384 0C18.8499 0 20 1.14911 20 2.56159L19.999 17.4384C19.999 18.85 18.8498 20 17.4374 20H2.56159C1.15014 19.9999 6.30826e-06 18.8508 0 17.4384V2.56159C6.17348e-06 1.15001 1.1493 9.98817e-05 2.56159 0H17.4384ZM2.56159 0.87251C1.63002 0.872609 0.872516 1.63 0.87251 2.56159V17.4394C0.872512 18.371 1.63002 19.1294 2.56159 19.1295H6.37648V0.87251H2.56159ZM7.24801 0.87251V19.1295H17.4384C18.3701 19.1295 19.1275 18.3711 19.1275 17.4394V2.56159C19.1275 1.62993 18.3701 0.87251 17.4384 0.87251H7.24801Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const HistoryIcon: FC<IconProps> = ({ size = 20, color = '#0D316E' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 19" overflow="visible">
    <path
      d="M9.1473 0C4.10024 0 0 4.25894 0 9.50064C0 14.7423 4.10147 19 9.1473 19C13.1698 19 16.5923 16.2987 17.8149 12.5433V12.5421C17.8953 12.2929 17.7657 12.0225 17.527 11.9378C17.4106 11.8967 17.2846 11.9054 17.1743 11.9615C17.0651 12.0175 16.9811 12.116 16.9416 12.2368C15.8439 15.6109 12.7728 18.0431 9.14739 18.0431C4.59938 18.0431 0.92265 14.224 0.92265 9.50067C0.92265 4.7773 4.59969 0.955675 9.14739 0.955675C13.3462 0.955675 16.8 4.21399 17.3061 8.43051L16.0393 7.35522C15.9445 7.27547 15.8221 7.23685 15.701 7.24931C15.5798 7.26301 15.467 7.32531 15.3902 7.42375C15.3135 7.52218 15.2763 7.64927 15.2895 7.77513C15.3015 7.90097 15.3615 8.0181 15.4562 8.09783L17.5424 9.86839C17.5556 9.87836 17.5688 9.88708 17.5832 9.89456C17.5964 9.90453 17.6108 9.91325 17.6252 9.92197C17.6828 9.95063 17.7452 9.96807 17.8088 9.97056C17.8172 9.9743 17.8256 9.9768 17.8351 9.97804C17.8411 9.9768 17.8483 9.97555 17.8555 9.97306C17.9191 9.96932 17.9815 9.95188 18.0391 9.92073C18.0535 9.9145 18.0667 9.90702 18.0799 9.8983C18.1207 9.87213 18.1591 9.83849 18.1902 9.79987L19.895 7.63308C20.0569 7.42998 20.0281 7.12846 19.8326 6.96023H19.8314C19.6346 6.79327 19.3443 6.82442 19.1836 7.02876L18.2143 8.25981C17.6264 3.6047 13.7874 0.0012759 9.1473 0ZM9.1521 2.65769C9.02973 2.65769 8.91217 2.70753 8.82579 2.79724C8.73822 2.88695 8.69023 3.00906 8.68903 3.13615V9.30128L5.36732 12.7526H5.36852C5.18977 12.9395 5.18977 13.241 5.36852 13.4279C5.45489 13.5176 5.57246 13.5687 5.69483 13.5687C5.81719 13.5687 5.93476 13.5176 6.02113 13.4279L9.47613 9.83817V9.83693C9.5625 9.74846 9.61169 9.62635 9.61169 9.50051V3.13602C9.61169 2.87187 9.40642 2.65893 9.1521 2.65769Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const HomeIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 16" overflow="visible">
    <path
      d="M10 0C9.76996 0 9.40484 0.0146872 9.03118 0.301282L0.205752 7.07051C0.151942 7.10225 0.106196 7.14401 0.0715533 7.19301C0.0369102 7.242 0.0141619 7.29712 0.00481882 7.35469C-0.00452429 7.41227 -0.000248479 7.47099 0.0173625 7.52696C0.0349734 7.58292 0.0655165 7.63485 0.106964 7.67929C0.148411 7.72373 0.199814 7.75968 0.25776 7.78473C0.315706 7.80979 0.378869 7.82338 0.443055 7.82461C0.507241 7.82584 0.570982 7.81468 0.630046 7.79187C0.689109 7.76906 0.742144 7.73512 0.785628 7.69231L9.61106 0.923077C9.66874 0.878833 10.0072 0.820513 10 0.820513C9.97669 0.820513 10.0973 0.823097 10.1909 0.846154C10.2846 0.869216 10.3756 0.912823 10.3889 0.923077L19.2144 7.69231C19.2579 7.73512 19.3109 7.76906 19.37 7.79187C19.429 7.81468 19.4928 7.82584 19.5569 7.82461C19.6211 7.82338 19.6843 7.80979 19.7422 7.78473C19.8002 7.75968 19.8516 7.72373 19.893 7.67929C19.9345 7.63485 19.965 7.58292 19.9826 7.52696C20.0002 7.47099 20.0045 7.41227 19.9952 7.35469C19.9858 7.29712 19.9631 7.242 19.9284 7.19301C19.8938 7.14401 19.8481 7.10225 19.7942 7.07051L10.9688 0.301282C10.7837 0.159329 10.5926 0.0991426 10.4243 0.0576923C10.256 0.016242 10.1348 0 10 0ZM2.75155 7.71795C2.69211 7.71879 2.63344 7.73023 2.57889 7.75163C2.52433 7.77302 2.47496 7.80395 2.43359 7.84264C2.39222 7.88133 2.35966 7.92703 2.33778 7.97713C2.31589 8.02722 2.3051 8.08074 2.30603 8.13462V15.5897C2.30605 15.6985 2.35373 15.8029 2.43861 15.8798C2.52348 15.9568 2.63859 16 2.75862 16H17.2414C17.3614 16 17.4765 15.9568 17.5614 15.8798C17.6463 15.8029 17.694 15.6985 17.694 15.5897V8.13462C17.6948 8.08025 17.6837 8.02628 17.6614 7.97583C17.639 7.92538 17.6058 7.87947 17.5637 7.84075C17.5216 7.80203 17.4714 7.77129 17.4161 7.75031C17.3607 7.72932 17.3014 7.71852 17.2414 7.71852C17.1814 7.71852 17.122 7.72932 17.0667 7.75031C17.0114 7.77129 16.9612 7.80203 16.9191 7.84075C16.877 7.87947 16.8438 7.92538 16.8214 7.97583C16.799 8.02628 16.7879 8.08025 16.7888 8.13462V15.1795H3.21121V8.13462C3.21216 8.07966 3.20091 8.02509 3.17815 7.97415C3.15539 7.92321 3.12156 7.87694 3.07869 7.83807C3.03582 7.79921 2.98477 7.76855 2.92857 7.74791C2.87237 7.72728 2.81217 7.71709 2.75155 7.71795Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const FolderIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 21 16" overflow="visible">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.76562 0C6.98665 0.000449569 7.17377 0.157559 7.20801 0.370117L7.56738 2.60156L7.57031 2.61621H17.3574C17.6057 2.61635 17.8066 2.81309 17.8066 3.05469V4.4502H19.5898C19.7263 4.45024 19.8572 4.51039 19.9424 4.61426C20.0273 4.71812 20.059 4.85482 20.0293 4.98438L17.791 14.6719C17.7447 14.8712 17.5625 15.0136 17.3525 15.0137H0.449219C0.200995 15.0135 0.000222789 14.8166 1.17097e-10 14.5752V0.438477C-2.7074e-06 0.322544 0.0469842 0.211075 0.130859 0.128906C0.214873 0.0468425 0.329199 0.000178343 0.448242 0H6.76562ZM19.5801 4.46484C19.7121 4.46488 19.8377 4.52364 19.9199 4.62402C20.002 4.72446 20.0327 4.85628 20.0039 4.98145L17.7676 14.668C17.7231 14.8605 17.5471 14.998 17.3438 14.998H17.3525C17.5559 14.998 17.7318 14.8605 17.7764 14.668L20.0146 4.98145C20.0435 4.85638 20.0127 4.72441 19.9307 4.62402C19.8484 4.52364 19.7219 4.46488 19.5898 4.46484H19.5801ZM0.898438 14.1367H16.9941L16.9971 14.123L19.0244 5.34863L19.0293 5.32715H3.34961L2.52637 8.45117C2.46459 8.68485 2.22069 8.8265 1.98047 8.7666C1.74005 8.70616 1.59469 8.46644 1.65625 8.23242L2.56641 4.7793C2.61747 4.58592 2.79678 4.4502 3.00195 4.4502H16.9082V3.49316H7.18555C6.96432 3.49297 6.77653 3.33477 6.74219 3.12207L6.38281 0.891602L6.37988 0.875977H0.897461L0.898438 14.1367ZM2.51074 8.44727L3.33789 5.31152H3.33594L2.51074 8.44727ZM17.4365 2.63965C17.6344 2.67935 17.7822 2.85091 17.7822 3.05469V4.46484H17.791V3.05469C17.791 2.82171 17.5978 2.63099 17.3574 2.63086H17.3486L17.4365 2.63965ZM6.76172 0.0146484C6.97553 0.0149111 7.15625 0.16708 7.18945 0.37207L7.54883 2.60352L7.55273 2.63086H7.55664L7.55273 2.60352L7.19336 0.37207C7.16016 0.167206 6.97938 0.0150977 6.76562 0.0146484H6.76172Z"
      fill={color}
    />
    <path
      d="M19.7471 4.45117L19.7441 4.45801L19.7422 4.46484C19.7101 4.45396 19.6758 4.44712 19.6416 4.44336L19.6436 4.42773C19.6788 4.43161 19.7139 4.43991 19.7471 4.45117Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const SearchIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 20" overflow="visible">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.9682 0C12.3571 0 15.9303 3.57507 15.9295 7.9682C15.9295 9.86858 15.2462 11.7111 14.0059 13.1533L13.9547 13.2134L13.9055 13.2695L19.8683 19.2314C20.0439 19.407 20.0439 19.6928 19.8683 19.8683C19.6928 20.0439 19.407 20.0439 19.2314 19.8683L13.2705 13.9084L13.2144 13.9557L13.1543 14.0049L13.1533 14.0059C11.7112 15.2472 9.86858 15.9305 7.9682 15.9305C3.57403 15.9303 0 12.3571 0 7.9682C0.000115907 5.77183 0.894781 3.78046 2.3371 2.3371C3.77938 0.893801 5.77178 0.000115775 7.9682 0ZM7.9682 0.901764C4.07224 0.901995 0.901995 4.07224 0.901764 7.9682C0.901764 11.8603 4.07219 15.0265 7.9682 15.0268C9.85841 15.0268 11.6321 14.2946 12.9633 12.9633C14.2935 11.6321 15.0268 9.85844 15.0268 7.9682C15.0265 4.07219 11.8603 0.901764 7.9682 0.901764Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const LibraryIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 21" overflow="visible">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.4368 0C19.2116 5.74858e-05 19.8528 0.615017 19.8528 1.38183V19.5684C19.8528 19.8158 19.6471 20.0078 19.4065 20.0078C19.1662 20.0076 18.9603 19.8157 18.9602 19.5684V19.0508L0.892578 19.0689V19.5865C0.892565 19.834 0.686808 20.0259 0.446289 20.0259C0.205951 20.0257 1.26701e-05 19.8338 0 19.5865V1.39997C0 0.633315 0.641298 0.018385 1.41602 0.0181332L18.4368 0ZM0.892578 18.191L9.12138 18.1729C9.12075 18.1599 9.11942 18.1469 9.11942 18.1338V12.3955C9.11942 11.8863 9.54318 11.4779 10.0569 11.4775H12.0257C12.2065 11.4776 12.3746 11.5301 12.5169 11.6172C12.6592 11.5298 12.828 11.4777 13.0091 11.4775H14.9778C15.4879 11.4775 15.9163 11.8822 15.9163 12.3955V18.1338C15.9163 18.147 15.915 18.16 15.9143 18.1729H18.9602V9.96485L0.892578 9.98298V18.191ZM10.0569 12.3555C10.0268 12.3558 10.011 12.3797 10.011 12.3955V18.1338C10.011 18.1496 10.0268 18.1726 10.0569 18.1729H12.0257C12.0559 18.1727 12.0715 18.1496 12.0716 18.1338V12.3955C12.0716 12.3797 12.0559 12.3557 12.0257 12.3555H10.0569ZM13.0091 12.3555C12.9792 12.3559 12.9632 12.3798 12.9632 12.3955V18.1338C12.9632 18.1495 12.9792 18.1724 13.0091 18.1729H14.9778C15.0082 18.1728 15.0237 18.1497 15.0237 18.1338V12.3955C15.0237 12.3796 15.0083 12.3555 14.9778 12.3555H13.0091ZM1.41602 0.897039C1.12249 0.897289 0.892578 1.12823 0.892578 1.39997V9.10505L4.20051 9.08692C4.19988 9.07396 4.19856 9.06096 4.19856 9.04786V3.3086C4.19866 2.79947 4.62241 2.39198 5.13606 2.3916H7.10481C7.28555 2.39164 7.45381 2.44334 7.59601 2.53028C7.73843 2.4429 7.90798 2.39169 8.08918 2.3916H10.0569C10.5669 2.3916 10.9953 2.7954 10.9954 3.3086V9.04786C10.9954 9.06106 10.9941 9.07407 10.9934 9.08692H18.9602L18.9612 1.38183C18.9612 1.11004 18.7316 0.878958 18.4378 0.878906L1.41602 0.897039ZM5.13606 3.26953C5.10606 3.26986 5.09022 3.29281 5.09016 3.3086V9.04786C5.09044 9.06363 5.10628 9.0866 5.13606 9.08692H7.10481C7.13482 9.08679 7.15041 9.06369 7.1507 9.04786V3.3086C7.15065 3.29275 7.13506 3.26967 7.10481 3.26953H5.13606ZM8.08918 3.26953C8.05906 3.26977 8.04236 3.29278 8.0423 3.3086V9.04786C8.04258 9.06366 8.05928 9.08668 8.08918 9.08692H10.0569C10.0871 9.08692 10.1025 9.06374 10.1028 9.04786V3.3086C10.1028 3.2927 10.0873 3.26953 10.0569 3.26953H8.08918Z"
      fill={color}
    />
  </SimpleIconSvg>
);

// ===========================================================================
// ACTION / FEATURE ICONS
// ===========================================================================

export const ChatIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 18" overflow="visible">
    <path
      d="M6.97363 12.999C6.90401 13.0322 6.84252 13.0809 6.79492 13.1426L6.87598 13.0596L6.97363 12.999Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.7399 0C18.9839 0.000248919 19.9996 1.08314 19.9996 2.42285V11.4229C19.9996 12.7625 18.9839 13.8455 17.7399 13.8457H7.43066L7.41602 13.8652L4.43652 17.7246C4.34598 17.8361 4.2101 17.9004 4.07031 17.9004C4.02608 17.9004 3.97552 17.8912 3.92871 17.8779C3.74487 17.8218 3.62014 17.6565 3.62012 17.4766V13.8457H2.25977C1.01574 13.8455 1.20477e-05 12.7625 0 11.4229V2.42285C0 1.08314 1.01573 0.000249195 2.25977 0H17.7399ZM2.25977 0.847656C1.50038 0.847926 0.900391 1.56452 0.900391 2.42285V11.4229C0.900402 12.286 1.50048 12.9978 2.25977 12.998H4.06543C4.31529 12.9982 4.51562 13.1915 4.51562 13.4219V16.1738L4.60449 16.0576L6.83496 13.1729L6.90723 13.0986C6.98788 13.034 7.09126 12.998 7.2002 12.998H17.7399C18.4992 12.9978 19.0992 12.286 19.0992 11.4229V2.42285C19.0992 1.56438 18.4941 0.847928 17.7399 0.847656H2.25977Z"
      fill={color}
    />
  </SimpleIconSvg>
);

// ChatIcon with shadow and custom styling for floating button
export const ChatIconFloating: FC<IconProps> = ({ size = 40, color = '#009AC5' }) => (
  <svg
    height={size}
    width={size}
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      filter: 'drop-shadow(0px 4px 12px rgba(0, 154, 197, 0.4))',
      cursor: 'pointer',
    }}
  >
    {/* Chat bubble with tail as one continuous path */}
    <path
      d="M11.75 9.25 H36.25 C38.5972 9.25 40.75 11.4028 40.75 13.75 V28.25 C40.75 30.5972 38.5972 32.75 36.25 32.75 H18 L12.5 38.5 L12.5 32.75 H11.75 C9.40279 32.75 7.25 30.5972 7.25 28.25 V13.75 C7.25 11.4028 9.40279 9.25 11.75 9.25 Z"
      fill={color}
      stroke="white"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    {/* Three dots inside the bubble */}
    <circle cx="16" cy="21" r="2" fill="white" />
    <circle cx="24" cy="21" r="2" fill="white" />
    <circle cx="32" cy="21" r="2" fill="white" />
  </svg>
);

export const ComposerIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 21 21">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.9122 0.22717C17.1894 -0.0499824 17.6114 -0.0772854 17.8549 0.166152L19.9726 2.2839C20.2161 2.52736 20.1888 2.94938 19.9116 3.22653L4.34811 18.79C4.26313 18.875 4.16059 18.9396 4.05009 18.9779L0.812339 20.0979C0.574981 20.18 0.32808 20.1314 0.167713 19.9711C0.00734617 19.8107 -0.0412076 19.5638 0.0408975 19.3265L1.16091 16.0887C1.19916 15.9782 1.26376 15.8756 1.34875 15.7907L16.9122 0.22717ZM17.292 1.61062L2.35994 16.5427L1.70617 18.4326L3.59606 17.7788L18.5281 2.84674L17.292 1.61062Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const QuoteIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 24 24">
    <path
      d="M11.5137 0C11.7114 8.33817e-06 11.9033 0.079109 12.0439 0.219727L23.7803 11.9561C23.9209 12.0967 24 12.2891 24 12.4883C23.9999 12.6859 23.9208 12.878 23.7803 13.0186L13.0186 23.7803C12.878 23.9208 12.6859 23.9999 12.4883 24C12.2891 24 12.0967 23.9209 11.9561 23.7803L0.219727 12.042C0.0792576 11.9029 8.57787e-06 11.7113 0 11.5137V0.75C3.97561e-06 0.550772 0.0791051 0.360348 0.219727 0.219727C0.360348 0.0791053 0.550787 3.99172e-06 0.75 0H11.5137ZM1.5 1.5V11.2012L12.4873 22.1895L22.1895 12.4863L11.2012 1.5H1.5ZM4.55566 5.91406C4.556 4.10251 7.27213 4.10251 7.27246 5.91406C7.27246 7.72606 4.55566 7.72606 4.55566 5.91406Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const AnalysisIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 20" overflow="visible">
    <rect x="0.5" y="0.5" width="8" height="8" rx="1.5" stroke={color} />
    <rect x="11.5" y="0.5" width="8" height="8" rx="1.5" stroke={color} />
    <rect x="11.5" y="11.5" width="8" height="8" rx="1.5" stroke={color} />
    <rect x="0.5" y="11.5" width="8" height="8" rx="1.5" stroke={color} />
  </SimpleIconSvg>
);

export const TemplateIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 21 19" overflow="visible">
    <path
      d="M12.4756 1.00443H12.5V1.00639C12.7015 1.0409 12.8555 1.21596 12.8555 1.42729C12.8551 1.6383 12.7013 1.81273 12.5 1.84721V1.85209H12.4561C12.4467 1.85271 12.4373 1.85501 12.4277 1.85502C12.4183 1.85502 12.4087 1.8527 12.3994 1.85209H2.25977C1.50049 1.85251 0.900391 2.56904 0.900391 3.42729V12.4273C0.90061 13.2902 1.50072 14.0021 2.25977 14.0025H4.06543C4.31529 14.0026 4.51562 14.196 4.51562 14.4263V17.1783L4.60449 17.0621L6.83496 14.1773L6.90723 14.1031C6.98781 14.0385 7.09135 14.0025 7.2002 14.0025H17.7402C18.4993 14.0021 19.0994 13.2902 19.0996 12.4273V7.95463H19.1045C19.1293 7.72959 19.3182 7.55432 19.5498 7.55424C19.7815 7.55424 19.9703 7.72956 19.9951 7.95463H20V12.4273C19.9998 13.7667 18.9841 14.8498 17.7402 14.8501H7.43066L7.41602 14.8697L4.43652 18.729C4.34598 18.8403 4.20993 18.9048 4.07031 18.9048C4.02617 18.9048 3.9754 18.8956 3.92871 18.8824C3.74514 18.8263 3.62035 18.6607 3.62012 18.481V14.8501H2.25977C1.01599 14.8497 0.000239091 13.7667 0 12.4273V3.42729C0 2.08766 1.01584 1.00482 2.25977 1.00443H12.3799C12.3956 1.00268 12.4116 0.999551 12.4277 0.999551C12.444 0.99956 12.4598 1.00266 12.4756 1.00443Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.0264 0.338418C16.1805 -0.112806 16.8195 -0.112806 16.9736 0.338418L17.6934 2.44779C17.7435 2.594 17.8586 2.70933 18.0049 2.75932L20.1143 3.47904C20.5653 3.63321 20.5654 4.27224 20.1143 4.42631L18.0049 5.14604C17.8586 5.1961 17.7434 5.31121 17.6934 5.45756L16.9736 7.56693L16.9385 7.64604C16.7408 8.01581 16.171 7.98948 16.0264 7.56693L15.3066 5.45756C15.2691 5.3478 15.195 5.25518 15.0986 5.19486L14.9951 5.14604L12.8857 4.42631C12.4346 4.27224 12.4347 3.63321 12.8857 3.47904L14.9951 2.75932C15.1048 2.72182 15.1975 2.64764 15.2578 2.55131L15.3066 2.44779L16.0264 0.338418ZM16.1299 2.72904C15.9927 3.13007 15.6774 3.44551 15.2764 3.58256L14.1924 3.95268L15.2764 4.32279C15.6273 4.44275 15.9127 4.69898 16.0703 5.02982L16.1299 5.17631L16.5 6.25932L16.8701 5.17631C17.0072 4.77514 17.3225 4.45991 17.7236 4.32279L18.8066 3.95268L17.7236 3.58256C17.3226 3.44551 17.0073 3.13006 16.8701 2.72904L16.5 1.64506L16.1299 2.72904Z"
      fill={color}
    />
    <path
      d="M3.88293 9.67893L5.9644 7.95216L3.88293 6.22539C3.70503 6.07774 3.67414 5.80611 3.81369 5.61786C3.95323 5.42963 4.20995 5.39694 4.38786 5.54459L6.87944 7.61176C6.97851 7.69404 7.03603 7.81915 7.03603 7.95216C7.03603 8.08516 6.97851 8.2114 6.87944 8.29256L4.38786 10.3597C4.31329 10.4217 4.22381 10.4521 4.1354 10.4521C4.01396 10.4521 3.89359 10.3958 3.81369 10.2865C3.67415 10.0982 3.70503 9.82658 3.88293 9.67893ZM7.64647 9.58651H15.3173C15.5431 9.58651 15.7263 9.78038 15.7263 10.0193C15.7263 10.2583 15.5431 10.4521 15.3173 10.4521H7.64647C7.42064 10.4521 7.23742 10.2583 7.23742 10.0193C7.23742 9.78038 7.42064 9.58651 7.64647 9.58651Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const ReviewIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 17 20" overflow="visible">
    <path
      d="M13.4905 0C14.8207 0.000179019 15.9048 1.06741 15.9251 2.39746L16.0061 7.6875C16.0098 7.9276 15.8175 8.12506 15.5774 8.12891C15.3374 8.13243 15.1398 7.94121 15.136 7.70117L15.055 2.41113C15.0419 1.55633 14.3454 0.86932 13.4905 0.869141H2.4827C1.61158 0.869141 0.907595 1.58108 0.917268 2.45215L0.870117 17.4561C0.879745 18.3133 1.57731 19.0037 2.43457 19.0039H7.93555C8.1755 19.0042 8.37012 19.1984 8.37012 19.4385C8.37012 19.6785 8.1755 19.8727 7.93555 19.873H2.43457C1.10062 19.8728 0.0149772 18.7997 0 17.4658L0.048127 2.46191C0.0330766 1.10661 1.12731 0 2.4827 0H13.4905Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.5491 6.94336C12.3104 6.94354 14.5491 9.18205 14.5491 11.9434C14.5491 13.1399 14.127 14.237 13.4261 15.0977C13.4414 15.1093 13.457 15.1208 13.471 15.1348L16.8567 18.5195C17.0263 18.6894 17.0265 18.966 16.8567 19.1357C16.6869 19.3052 16.4113 19.3043 16.2415 19.1348L12.8557 15.75C12.8452 15.7394 12.8367 15.7272 12.8274 15.7158C11.9495 16.4794 10.804 16.9433 9.5491 16.9434C6.78768 16.9434 4.5491 14.7048 4.5491 11.9434C4.5491 9.18194 6.78768 6.94336 9.5491 6.94336ZM9.5491 7.81348C7.26817 7.81348 5.41922 9.66242 5.41922 11.9434C5.41922 14.2243 7.26817 16.0732 9.5491 16.0732C11.8299 16.0731 13.679 14.2242 13.679 11.9434C13.679 9.66253 11.8299 7.81366 9.5491 7.81348Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const SideBySideIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 21">
    <path
      d="M4.69638 9.66943C4.86606 9.50024 5.14085 9.50042 5.31064 9.66943C5.48037 9.83916 5.48105 10.1148 5.31162 10.2847L1.48447 14.1128H19.339C19.5789 14.113 19.7733 14.3075 19.7735 14.5474C19.7734 14.7873 19.5789 14.9827 19.339 14.9829H1.43564L5.79697 19.3442C5.96614 19.5139 5.966 19.7887 5.79697 19.9585C5.62725 20.1282 5.35164 20.1289 5.18173 19.9595L0.127047 14.9048C-0.00171281 14.7759 -0.0325307 14.5864 0.0342735 14.4282C0.0517401 14.351 0.0894103 14.2775 0.149508 14.2173L4.69638 9.66943Z"
      fill={color}
    />
    <path
      d="M13.9767 0.127442C14.1463 -0.0420144 14.422 -0.0426194 14.5919 0.126465L19.6466 5.18115C19.7753 5.30994 19.806 5.49957 19.7394 5.65772C19.7219 5.7349 19.6841 5.80848 19.6241 5.86865L15.0772 10.4165C14.9076 10.5857 14.6328 10.5855 14.463 10.4165C14.2935 10.2468 14.2927 9.9711 14.462 9.80127L18.2892 5.97315H0.434664C0.194795 5.97287 0.00032576 5.77845 9.38454e-05 5.53857C0.000479 5.29883 0.194889 5.1033 0.434664 5.10303H18.338L13.9767 0.7417C13.8077 0.571992 13.8077 0.297129 13.9767 0.127442Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const ComparisonIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 20">
    <path
      d="M13.0928 0.00488281H17.5215C18.8663 0.00488281 19.957 1.09464 19.957 2.43945V17.5703C19.9569 18.915 18.8662 20.0049 17.5215 20.0049H10.3857V20H2L1.7959 19.9893C0.854359 19.8938 0.106215 19.1456 0.0107422 18.2041L0 18V2C2.57753e-07 0.895431 0.895431 5.63724e-08 2 0H13C13.0311 5.57936e-06 13.062 0.00346945 13.0928 0.00488281ZM14.6523 0.874023C14.8713 1.19478 15 1.58233 15 2V18L14.9893 18.2041C14.9544 18.5478 14.831 18.8649 14.6445 19.1348H17.5215C18.3857 19.1348 19.0867 18.4345 19.0869 17.5703V2.43945C19.0869 1.57513 18.3858 0.874023 17.5215 0.874023H14.6523ZM2 0.870117C1.37592 0.870117 0.870117 1.37592 0.870117 2V18C0.870117 18.6241 1.37592 19.1299 2 19.1299H13C13.6239 19.1297 14.1299 18.624 14.1299 18V2C14.1299 1.402 13.665 0.913887 13.0771 0.874023H12.0215V0.870117H2ZM10.5439 14.1318C10.784 14.1321 10.9785 14.3273 10.9785 14.5674C10.9783 14.8073 10.7838 15.0017 10.5439 15.002H4.41309C4.17318 15.0017 3.97875 14.8073 3.97852 14.5674C3.97852 14.3273 4.17304 14.1321 4.41309 14.1318H10.5439ZM7.41309 5.00195C7.65319 5.00195 7.8484 5.19648 7.84863 5.43652V8.13184H10.5439C10.784 8.13207 10.9785 8.32728 10.9785 8.56738C10.9783 8.80729 10.7838 9.00172 10.5439 9.00195H7.84863V11.5674C7.8484 11.8074 7.65319 12.002 7.41309 12.002C7.17318 12.0017 6.97875 11.8073 6.97852 11.5674V9.00195H4.41309C4.17318 9.00172 3.97875 8.80729 3.97852 8.56738C3.97852 8.32728 4.17304 8.13207 4.41309 8.13184H6.97852V5.43652C6.97875 5.19662 7.17318 5.00218 7.41309 5.00195Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const UserGuideIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 26 26">
    <path
      d="M12.9971 0C20.1648 0 26.0008 5.82742 26.001 12.9951C26.001 20.163 20.1649 26.001 12.9971 26.001C5.82944 26.0007 0 20.1628 0 12.9951C0.000169548 5.82758 5.82955 0.000260252 12.9971 0ZM12.9971 2.16699C7.00066 2.16725 2.16716 6.99869 2.16699 12.9951C2.16699 18.9917 7.00056 23.8318 12.9971 23.832C18.9938 23.832 23.835 18.9918 23.835 12.9951C23.8348 6.99853 18.9937 2.16699 12.9971 2.16699ZM13 10.7803C13.143 10.7797 13.2848 10.8069 13.417 10.8613C13.5492 10.9158 13.6694 10.9965 13.7705 11.0977C13.8715 11.1987 13.9514 11.319 14.0059 11.4512C14.0603 11.5834 14.0885 11.7252 14.0879 11.8682V18.4658C14.0876 18.6083 14.0587 18.7494 14.0039 18.8809C13.9491 19.0123 13.8695 19.132 13.7686 19.2324C13.6675 19.3329 13.5468 19.4127 13.415 19.4668C13.2834 19.5207 13.1423 19.5484 13 19.5479C12.7138 19.5467 12.4395 19.4321 12.2373 19.2295C12.0351 19.0269 11.9215 18.7521 11.9209 18.4658V11.8682C11.9203 11.7259 11.948 11.5848 12.002 11.4531C12.0559 11.3215 12.1351 11.2016 12.2354 11.1006C12.3356 10.9996 12.4547 10.9192 12.5859 10.8643C12.7171 10.8094 12.8578 10.7808 13 10.7803ZM13 6.67676C13.3878 6.67515 13.7607 6.82756 14.0361 7.10059C14.3115 7.3736 14.467 7.74506 14.4688 8.13281C14.4695 8.32576 14.4318 8.51688 14.3584 8.69531C14.2849 8.87386 14.1766 9.03632 14.04 9.17285C13.9035 9.30938 13.741 9.41769 13.5625 9.49121C13.3841 9.56463 13.193 9.60236 13 9.60156C12.6123 9.59981 12.2408 9.44429 11.9678 9.16895C11.6948 8.89354 11.5423 8.52061 11.5439 8.13281C11.5457 7.74722 11.7 7.37813 11.9727 7.10547C12.2453 6.83281 12.6144 6.6785 13 6.67676Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const TeamIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 25 25" overflow="visible">
    <WeightedPath
      d="M15.4209 0.125C18.6783 0.12511 21.3203 2.77501 21.3203 6.03711C21.3202 8.10512 20.2543 9.91838 18.6494 10.9727C19.7961 11.4158 20.8508 12.0887 21.751 12.9834L21.752 12.9844C23.4416 14.6841 24.375 16.9346 24.375 19.333L24.3721 20.249C24.3718 20.6847 24.0165 21.041 23.5811 21.041H17.876C17.9778 21.5756 18.0312 22.1212 18.0312 22.666V23.582C18.0312 24.0177 17.6756 24.3748 17.2402 24.375H0.916016C0.480589 24.3748 0.125 24.0178 0.125 23.582V22.666C0.125179 18.8324 2.47079 15.5943 5.8291 14.2979C4.23158 13.243 3.17969 11.4299 3.17969 9.37012C3.17986 6.10686 5.82317 3.45913 9.0791 3.45898C9.42353 3.45898 9.75374 3.49968 10.0801 3.55762C11.0383 1.48539 13.0969 0.125 15.4209 0.125ZM8.96973 15.2793C8.94397 15.2793 8.92583 15.2791 8.90723 15.2812L8.89648 15.2822C4.85067 15.4006 1.70996 18.6401 1.70996 22.668V22.7891H16.4434V22.6602C16.4434 21.9173 16.3317 21.1817 16.1152 20.4717C15.9721 20.0009 15.7685 19.5479 15.5254 19.1064L15.5244 19.1045C15.3809 18.8354 15.216 18.5738 15.0332 18.3242V18.3232C14.808 18.0121 14.5658 17.7109 14.292 17.4365C12.9419 16.084 11.1524 15.3278 9.25 15.2822V15.2812C9.21447 15.2812 9.17898 15.2808 9.14648 15.2793C9.11475 15.2778 9.08471 15.2756 9.05371 15.2754C9.02148 15.2787 8.99224 15.2793 8.96973 15.2793ZM15.416 11.957C15.0542 11.9571 14.6945 11.9806 14.3408 12.0332C14.33 12.0521 14.3209 12.0713 14.3105 12.0879C14.2886 12.1231 14.2696 12.152 14.252 12.1846C14.1794 12.3187 14.104 12.4565 14.0215 12.5869C13.9515 12.6973 13.8766 12.801 13.8027 12.9014C13.7066 13.031 13.6044 13.1537 13.5049 13.2734L13.5 13.2793C13.4182 13.3676 13.329 13.457 13.2432 13.543C13.1362 13.6501 13.0264 13.7608 12.9014 13.8652L12.9004 13.8643C12.8019 13.9557 12.6982 14.0321 12.6016 14.1035L12.6006 14.1045C12.5079 14.1722 12.4114 14.2372 12.3135 14.3027C12.4706 14.3611 12.6219 14.4281 12.7725 14.4951H12.7734C12.8993 14.5437 13.0163 14.6005 13.1328 14.6621C13.3007 14.7506 13.4618 14.844 13.6211 14.9375H13.6221C13.7294 14.9975 13.8399 15.0595 13.9443 15.1289C14.1219 15.2454 14.285 15.3687 14.4512 15.4951C14.4926 15.5265 14.5335 15.5551 14.5771 15.5859C14.6196 15.616 14.6645 15.6475 14.708 15.6836L15.0723 15.9941L15.417 16.3203L15.4189 16.3213C15.5406 16.4464 15.6565 16.5738 15.7695 16.7031L16.1006 17.0986L16.1016 17.0996C16.1375 17.1456 16.1706 17.1934 16.2021 17.2383L16.2959 17.3672L16.2979 17.3691C16.4306 17.5499 16.5667 17.7399 16.6895 17.9385H16.6885L16.8906 18.2891L16.8926 18.291C16.9921 18.4704 17.0942 18.6545 17.1826 18.8457L17.3506 19.2373L17.4443 19.4561H22.7861L22.7871 19.3428L22.7783 18.9746C22.689 17.1416 21.9327 15.4315 20.623 14.1191C19.2324 12.7256 17.3832 11.957 15.416 11.957ZM9.08008 5.0459C6.70293 5.0461 4.7649 6.98752 4.76465 9.37012C4.76465 11.6884 6.57832 13.5789 8.89258 13.6885H9.08008C10.8419 13.6885 12.404 12.6365 13.0713 11.0137L13.1895 10.7002C13.3254 10.2651 13.3965 9.81695 13.3965 9.37012C13.3963 7.48082 12.1314 5.77178 10.3242 5.22656H10.3232C9.93028 5.10423 9.50883 5.0459 9.08008 5.0459ZM15.4219 1.71191C13.7928 1.71194 12.336 2.61876 11.5771 4.02539C11.7791 4.12141 11.9754 4.23066 12.1631 4.3457C12.1846 4.3585 12.2058 4.37271 12.2217 4.38379L12.2637 4.41211L12.2695 4.41602C12.3709 4.48397 12.473 4.55495 12.5732 4.62988L12.8682 4.86621L12.875 4.87207L12.8975 4.8916C12.9055 4.8984 12.9159 4.9086 12.9277 4.91992C13.1241 5.09005 13.3065 5.27268 13.4746 5.46094C13.482 5.4683 13.4824 5.46871 13.4883 5.47461L13.4941 5.47949L13.4932 5.48047C13.6628 5.67095 13.8191 5.88132 13.96 6.08887L13.9932 6.13867L14.0137 6.16895L14.0195 6.17676L14.2109 6.49707C14.2705 6.60439 14.3268 6.71308 14.3809 6.8252H14.3799C14.3907 6.84696 14.4 6.8668 14.4082 6.88477L14.4336 6.93848L14.4346 6.94043C14.537 7.15854 14.6244 7.39141 14.6992 7.62109H14.7002C14.7091 7.64695 14.7149 7.67217 14.7197 7.69141C14.7249 7.71211 14.7286 7.72713 14.7334 7.74121L14.7363 7.74902C14.8035 7.99287 14.8586 8.23146 14.8994 8.4834H14.8975C14.9019 8.50294 14.9059 8.52095 14.9072 8.53223L14.9111 8.56152L14.9131 8.57129L14.9395 8.77148C14.9625 8.97187 14.9746 9.17312 14.9746 9.37793C14.9746 9.65071 14.954 9.93116 14.9131 10.2041C14.9099 10.2276 14.9067 10.2515 14.9033 10.2744C14.9006 10.2931 14.897 10.3112 14.8945 10.3291C15.0694 10.3494 15.2471 10.3613 15.4219 10.3613C17.7992 10.3613 19.7381 8.41987 19.7383 6.03711C19.7383 3.65417 17.7994 1.71191 15.4219 1.71191Z"
      color={color}
      weight={0.29}
    />
  </SimpleIconSvg>
);

export const LogoutIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 23 23">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.0385 0H2.65385C1.94947 0 1.27495 0.279759 0.777358 0.777358C0.279765 1.27495 0 1.94947 0 2.65385V20.3462C0 21.0505 0.279759 21.725 0.777358 22.2226C1.27495 22.7202 1.94947 23 2.65385 23H15.0385C15.5272 23 15.9231 22.6041 15.9231 22.1154C15.9231 21.6266 15.5272 21.2308 15.0385 21.2308H2.65385C2.41942 21.2308 2.19385 21.1379 2.02799 20.972C1.86213 20.8062 1.76924 20.5806 1.76924 20.3462V2.65386C1.76924 2.41944 1.86213 2.19386 2.02799 2.028C2.19386 1.86214 2.41943 1.76925 2.65385 1.76925H15.0385C15.5272 1.76925 15.9231 1.3734 15.9231 0.884638C15.9231 0.395877 15.5272 0 15.0385 0ZM19.9801 10.6154H7.96154C7.47278 10.6154 7.07692 11.0112 7.07692 11.5C7.07692 11.9888 7.47278 12.3846 7.96154 12.3846H19.9801L17.0664 15.2972C16.7214 15.6422 16.7214 16.2039 17.0664 16.5489C17.4114 16.8939 17.9731 16.8939 18.3182 16.5489L22.7412 12.1259C23.0863 11.7808 23.0863 11.2191 22.7412 10.8741L18.3182 6.45104C17.9731 6.10603 17.4114 6.10603 17.0664 6.45104C16.7214 6.79605 16.7214 7.35776 17.0664 7.70278L19.9801 10.6154Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const ScribeIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 13 21">
    <path
      d="M0.434998 10.4346C0.434998 13.7483 3.12129 16.4346 6.435 16.4346C9.74871 16.4346 12.435 13.7483 12.435 10.4346M6.37 16.4346V19.9783M0.434998 19.9996L12.435 19.9996M6.435 14.4346C3.94972 14.4346 1.935 12.4199 1.935 9.93457V4.93457C1.935 2.44929 3.94972 0.43457 6.435 0.43457C8.92028 0.43457 10.935 2.44929 10.935 4.93457V9.93457C10.935 12.4199 8.92028 14.4346 6.435 14.4346Z"
      stroke={color}
      strokeWidth="0.87"
      strokeLinecap="round"
    />
  </SimpleIconSvg>
);

export const WorkstudioIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 14">
    <path
      d="M7.56543 9.62988C7.80548 9.63011 8 9.82533 8 10.0654C7.99977 10.3053 7.80533 10.4998 7.56543 10.5H3.43457C3.19467 10.4998 3.00023 10.3053 3 10.0654C3 9.82533 3.19452 9.63011 3.43457 9.62988H7.56543Z"
      fill={color}
    />
    <path
      d="M7.56543 6.62988C7.80548 6.63011 8 6.82533 8 7.06543C7.99977 7.30533 7.80533 7.49977 7.56543 7.5H3.43457C3.19467 7.49977 3.00023 7.30533 3 7.06543C3 6.82533 3.19452 6.63012 3.43457 6.62988H7.56543Z"
      fill={color}
    />
    <path
      d="M7.56543 3.62988C7.80548 3.63011 8 3.82533 8 4.06543C7.99977 4.30533 7.80533 4.49977 7.56543 4.5H3.43457C3.19467 4.49977 3.00023 4.30533 3 4.06543C3 3.82533 3.19452 3.63011 3.43457 3.62988H7.56543Z"
      fill={color}
    />
    <path
      d="M16.8652 9.62988C17.1053 9.63011 17.2998 9.82533 17.2998 10.0654C17.2996 10.3053 17.1051 10.4998 16.8652 10.5H12.7344C12.4946 10.4997 12.3 10.3053 12.2998 10.0654C12.2998 9.82539 12.4944 9.63021 12.7344 9.62988H16.8652Z"
      fill={color}
    />
    <path
      d="M16.8652 6.62988C17.1053 6.63011 17.2998 6.82533 17.2998 7.06543C17.2996 7.30533 17.1051 7.49977 16.8652 7.5H12.7344C12.4946 7.49967 12.3 7.30527 12.2998 7.06543C12.2998 6.82539 12.4944 6.63021 12.7344 6.62988H16.8652Z"
      fill={color}
    />
    <path
      d="M16.8652 3.62988C17.1053 3.63011 17.2998 3.82533 17.2998 4.06543C17.2996 4.30533 17.1051 4.49977 16.8652 4.5H12.7344C12.4946 4.49967 12.3 4.30527 12.2998 4.06543C12.2998 3.82539 12.4944 3.63021 12.7344 3.62988H16.8652Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.2041 0.0107422C19.2128 0.113005 20 0.96435 20 2V12C20 13.0357 19.2128 13.887 18.2041 13.9893L18 14H2C0.96435 14 0.113005 13.2128 0.0107422 12.2041L0 12V2C0 0.895431 0.895431 1.61064e-08 2 0H18L18.2041 0.0107422ZM2 0.870117C1.37592 0.870117 0.870117 1.37592 0.870117 2V12C0.870117 12.6241 1.37592 13.1299 2 13.1299H9.58008V0.870117H2ZM10.4297 13.1299H18C18.6241 13.1299 19.1299 12.6241 19.1299 12V2C19.1299 1.37592 18.6241 0.870117 18 0.870117H10.4297V13.1299Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const HandStopIcon = createStrokeIcon({
  paths: [
    'M8 13v-7.5a1.5 1.5 0 0 1 3 0v6.5',
    'M11 5.5v-2a1.5 1.5 0 1 1 3 0v8.5',
    'M14 5.5a1.5 1.5 0 0 1 3 0v6.5',
    'M17 7.5a1.5 1.5 0 0 1 3 0v8.5a6 6 0 0 1 -6 6h-2h.208a6 6 0 0 1 -5.012 -2.7a69.74 69.74 0 0 1 -.196 -.3c-.312 -.479 -1.407 -2.388 -3.286 -5.728a1.5 1.5 0 0 1 .536 -2.022a1.867 1.867 0 0 1 2.28 .28l1.47 1.47',
  ],
});

export const BoltIcon = createStrokeIcon({
  paths: ['M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11'],
});

export const CheckIcon = createStrokeIcon({
  paths: ['M5 12l5 5l10 -10'],
});

// Tabler outline icons used by the Context menu.
export const BanIcon = createStrokeIcon({
  paths: ['M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0', 'M5.7 5.7l12.6 12.6'],
});

export const StackIcon = createStrokeIcon({
  paths: ['M12 4l-8 4l8 4l8 -4l-8 -4', 'M4 12l8 4l8 -4', 'M4 16l8 4l8 -4'],
});

export const RepeatIcon = createStrokeIcon({
  paths: [
    'M4 12v-3a3 3 0 0 1 3 -3h13m-3 -3l3 3l-3 3',
    'M20 12v3a3 3 0 0 1 -3 3h-13m3 3l-3 -3l3 -3',
  ],
});

export const AdjustmentsIcon = createStrokeIcon({
  paths: [
    'M12 6a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M4 6l8 0',
    'M16 6l4 0',
    'M6 12a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M4 12l2 0',
    'M10 12l10 0',
    'M15 18a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M4 18l11 0',
    'M19 18l1 0',
  ],
});

// ===========================================================================
// PLATFORM ICONS
// ===========================================================================

export interface PlatformIconProps extends IconProps {
  platform: 'facebook' | 'whatsapp' | 'slack' | 'telegram' | 'line' | 'widget';
  grayscale?: boolean;
}

export const PlatformIcon: FC<PlatformIconProps> = ({ platform, size = 24, grayscale = false }) => {
  const style: React.CSSProperties = grayscale ? { filter: 'grayscale(100%)' } : {};

  switch (platform) {
    case 'facebook':
      return (
        <Icon
          style={{ display: 'block' }}
          component={() => (
            <svg style={style} height={size} width={size} viewBox="100 100 820 820">
              <defs>
                <radialGradient
                  cx="19.2474387%"
                  cy="99.4651948%"
                  fx="19.2474387%"
                  fy="99.4651948%"
                  id="radialGradient-1"
                  r="108.959588%"
                >
                  <stop offset="0%" stopColor="#0099FF" />
                  <stop offset="60.9753877%" stopColor="#A033FF" />
                  <stop offset="93.482299%" stopColor="#FF5280" />
                  <stop offset="100%" stopColor="#FF7061" />
                </radialGradient>
              </defs>
              <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
                <rect fill="#FFFFFF" fillOpacity="0" height="1024" width="1024" x="0" y="0" />
                <g>
                  <path
                    d="M512,122 C286.668,122 112,287.056 112,510 C112,626.6144 159.792,727.3824 237.6224,796.984 C244.156,802.832 248.1,811.024 248.368,819.792 L250.5464,890.944 C251.2424,913.64 274.6856,928.408 295.4536,919.24 L374.848,884.192 C381.5784,881.224 389.12,880.672 396.212,882.624 C432.696,892.656 471.5264,898 512,898 C737.332,898 912,732.944 912,510 C912,287.056 737.332,122 512,122 Z"
                    fill="url(#radialGradient-1)"
                  />
                  <path
                    d="M271.8016,623.4688 L389.3016,437.0528 C407.992,407.3968 448.016,400.0128 476.06,421.0448 L569.5136,491.1352 C578.088,497.5672 589.8856,497.5328 598.424,491.0528 L724.6376,395.2648 C741.484,382.4808 763.4736,402.6408 752.2,420.5312 L634.7,606.9488 C616.008,636.6032 575.984,643.9888 547.9416,622.9552 L454.4856,552.8632 C445.912,546.4328 434.1136,546.4672 425.576,552.9472 L299.3616,648.7352 C282.516,661.5184 260.5256,641.3584 271.8016,623.4688 Z"
                    fill="#FFFFFF"
                  />
                </g>
              </g>
            </svg>
          )}
        />
      );

    case 'whatsapp':
      return (
        <Icon
          style={{ display: 'block' }}
          component={() => (
            <svg
              style={style}
              height={size}
              width={size}
              viewBox="0 0 24 24"
              enableBackground="new 0 0 24 24"
            >
              <path
                d="m20.52 3.449c-2.28-2.204-5.28-3.449-8.475-3.449-9.17 0-14.928 9.935-10.349 17.838l-1.696 6.162 6.335-1.652c2.76 1.491 5.021 1.359 5.716 1.447 10.633 0 15.926-12.864 8.454-20.307z"
                fill="#eceff1"
              />
              <path
                d="m12.067 21.751-.006-.001h-.016c-3.182 0-5.215-1.507-5.415-1.594l-3.75.975 1.005-3.645-.239-.375c-.99-1.576-1.516-3.391-1.516-5.26 0-8.793 10.745-13.19 16.963-6.975 6.203 6.15 1.848 16.875-7.026 16.875z"
                fill="#4caf50"
              />
              <path
                d="m17.507 14.307-.009.075c-.301-.15-1.767-.867-2.04-.966-.613-.227-.44-.036-1.617 1.312-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.293-.506.32-.578.878-1.634.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.576-.05-.997-.042-1.368.344-1.614 1.774-1.207 3.604.174 5.55 2.714 3.552 4.16 4.206 6.804 5.114.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345z"
                fill="#fafafa"
              />
            </svg>
          )}
        />
      );

    case 'slack':
      return (
        <Icon
          style={{ display: 'block' }}
          component={() => (
            <svg
              style={style}
              height={size}
              width={size}
              viewBox="0 0 24 24"
              enableBackground="new 0 0 24 24"
            >
              <path
                d="m8.843 12.651c-1.392 0-2.521 1.129-2.521 2.521v6.306c0 1.392 1.129 2.521 2.521 2.521s2.521-1.129 2.521-2.521v-6.306c-.001-1.392-1.13-2.521-2.521-2.521z"
                fill="#e91e63"
              />
              <path
                d="m.019 15.172c0 1.393 1.13 2.523 2.523 2.523s2.523-1.13 2.523-2.523v-2.523h-2.521c-.001 0-.001 0-.002 0-1.393 0-2.523 1.13-2.523 2.523z"
                fill="#e91e63"
              />
              <path
                d="m8.846-.001c-.001 0-.002 0-.003 0-1.393 0-2.523 1.13-2.523 2.523s1.13 2.523 2.523 2.523h2.521v-2.523c0-.001 0-.003 0-.005-.001-1.391-1.128-2.518-2.518-2.518z"
                fill="#00bcd4"
              />
              <path
                d="m2.525 11.37h6.318c1.393 0 2.523-1.13 2.523-2.523s-1.13-2.523-2.523-2.523h-6.318c-1.393 0-2.523 1.13-2.523 2.523s1.13 2.523 2.523 2.523z"
                fill="#00bcd4"
              />
              <path
                d="m21.457 6.323c-1.391 0-2.518 1.127-2.518 2.518v.005 2.523h2.521c1.393 0 2.523-1.13 2.523-2.523s-1.13-2.523-2.523-2.523c-.001 0-.002 0-.003 0z"
                fill="#4caf50"
              />
              <path
                d="m12.641 2.522v6.325c0 1.392 1.129 2.521 2.521 2.521s2.521-1.129 2.521-2.521v-6.325c0-1.392-1.129-2.521-2.521-2.521-1.392 0-2.521 1.129-2.521 2.521z"
                fill="#4caf50"
              />
              <g fill="#ff9800">
                <path d="m17.682 21.476c0-1.392-1.129-2.521-2.521-2.521h-2.521v2.523c.001 1.391 1.129 2.519 2.521 2.519s2.521-1.129 2.521-2.521z" />
                <path d="m21.479 12.649h-6.318c-1.393 0-2.523 1.13-2.523 2.523s1.13 2.523 2.523 2.523h6.318c1.393 0 2.523-1.13 2.523-2.523s-1.13-2.523-2.523-2.523z" />
              </g>
            </svg>
          )}
        />
      );

    case 'telegram':
      return (
        <Icon
          style={{ display: 'block' }}
          component={() => (
            <svg
              style={style}
              height={size}
              width={size}
              viewBox="0 0 24 24"
              enableBackground="new 0 0 24 24"
            >
              <circle cx="12" cy="12" fill="#039be5" r="12" />
              <path
                d="m5.491 11.74 11.57-4.461c.537-.194 1.006.131.832.943l.001-.001-1.97 9.281c-.146.658-.537.818-1.084.508l-3-2.211-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.121l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953z"
                fill="#fff"
              />
            </svg>
          )}
        />
      );

    case 'line':
      return (
        <Icon
          style={{ display: 'block' }}
          component={() => (
            <svg
              style={style}
              height={size}
              width={size}
              viewBox="0 0 24 24"
              enableBackground="new 0 0 24 24"
            >
              <path
                d="m12 .5c-6.615 0-12 4.398-12 9.803 0 4.841 4.27 8.897 10.035 9.668.391.083.923.26 1.058.594.12.303.079.771.038 1.087l-.164 1.026c-.045.303-.24 1.193 1.049.649 1.291-.542 6.916-4.104 9.436-7.019 1.724-1.9 2.548-3.847 2.548-6.005 0-5.405-5.385-9.803-12-9.803zm-4.697 13.017h-2.386c-.345 0-.63-.287-.63-.633v-4.801c0-.347.285-.634.63-.634.348 0 .63.287.63.634v4.167h1.756c.348 0 .629.285.629.634 0 .346-.282.633-.629.633zm2.466-.633c0 .346-.282.633-.631.633-.345 0-.627-.287-.627-.633v-4.801c0-.347.282-.634.63-.634.346 0 .628.287.628.634zm5.741 0c0 .272-.174.513-.432.6-.064.021-.133.031-.199.031-.211 0-.391-.091-.51-.252l-2.443-3.338v2.958c0 .346-.279.633-.631.633-.346 0-.626-.287-.626-.633v-4.8c0-.272.173-.513.43-.599.06-.023.136-.033.194-.033.195 0 .375.105.495.256l2.462 3.351v-2.975c0-.347.282-.634.63-.634.345 0 .63.287.63.634zm3.855-3.035c.349 0 .63.287.63.635 0 .347-.281.634-.63.634h-1.755v1.132h1.755c.349 0 .63.285.63.634 0 .346-.281.633-.63.633h-2.386c-.345 0-.627-.287-.627-.633v-4.801c0-.347.282-.634.63-.634h2.386c.346 0 .627.287.627.634 0 .351-.281.634-.63.634h-1.755v1.132z"
                fill="#4caf50"
              />
            </svg>
          )}
        />
      );

    case 'widget':
      return (
        <Icon
          style={{ display: 'block' }}
          component={() => (
            <svg
              style={style}
              height={size}
              width={size}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
            >
              <path
                fill="#fff"
                d="M34,24c0,5.521-4.479,10-10,10s-10-4.479-10-10s4.479-10,10-10S34,18.479,34,24z"
              />
              <linearGradient
                id="Pax8JcnMzivu8f~SZ~k1ya"
                x1="5.789"
                x2="31.324"
                y1="34.356"
                y2="20.779"
                gradientTransform="matrix(1 0 0 -1 0 50)"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#4caf50" />
                <stop offset=".489" stopColor="#4aaf50" />
                <stop offset=".665" stopColor="#43ad50" />
                <stop offset=".79" stopColor="#38aa50" />
                <stop offset=".892" stopColor="#27a550" />
                <stop offset=".978" stopColor="#11a050" />
                <stop offset="1" stopColor="#0a9e50" />
              </linearGradient>
              <path
                fill="url(#Pax8JcnMzivu8f~SZ~k1ya)"
                d="M31.33,29.21l-8.16,14.77C12.51,43.55,4,34.76,4,24C4,12.96,12.96,4,24,4v11 c-4.97,0-9,4.03-9,9s4.03,9,9,9C27.03,33,29.7,31.51,31.33,29.21z"
              />
              <linearGradient
                id="Pax8JcnMzivu8f~SZ~k1yb"
                x1="33.58"
                x2="33.58"
                y1="6"
                y2="34.797"
                gradientTransform="matrix(1 0 0 -1 0 50)"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#ffd747" />
                <stop offset=".482" stopColor="#ffd645" />
                <stop offset=".655" stopColor="#fed43e" />
                <stop offset=".779" stopColor="#fccf33" />
                <stop offset=".879" stopColor="#fac922" />
                <stop offset=".964" stopColor="#f7c10c" />
                <stop offset="1" stopColor="#f5bc00" />
              </linearGradient>
              <path
                fill="url(#Pax8JcnMzivu8f~SZ~k1yb)"
                d="M44,24c0,11.05-8.95,20-20,20h-0.84l8.17-14.79C32.38,27.74,33,25.94,33,24 c0-4.97-4.03-9-9-9V4c7.81,0,14.55,4.48,17.85,11C43.21,17.71,44,20.76,44,24z"
              />
              <linearGradient
                id="Pax8JcnMzivu8f~SZ~k1yc"
                x1="36.128"
                x2="11.574"
                y1="44.297"
                y2="28.954"
                gradientTransform="matrix(1 0 0 -1 0 50)"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#f7572f" />
                <stop offset=".523" stopColor="#f7552d" />
                <stop offset=".712" stopColor="#f75026" />
                <stop offset=".846" stopColor="#f7461b" />
                <stop offset=".954" stopColor="#f7390a" />
                <stop offset="1" stopColor="#f73100" />
              </linearGradient>
              <path
                fill="url(#Pax8JcnMzivu8f~SZ~k1yc)"
                d="M41.84,15H24c-4.97,0-9,4.03-9,9c0,1.49,0.36,2.89,1.01,4.13H16L7.16,13.26H7.14 C10.68,7.69,16.91,4,24,4C31.8,4,38.55,8.48,41.84,15z"
              />
              <linearGradient
                id="Pax8JcnMzivu8f~SZ~k1yd"
                x1="19.05"
                x2="28.95"
                y1="30.95"
                y2="21.05"
                gradientTransform="matrix(1 0 0 -1 0 50)"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#2aa4f4" />
                <stop offset="1" stopColor="#007ad9" />
              </linearGradient>
              <path
                fill="url(#Pax8JcnMzivu8f~SZ~k1yd)"
                d="M31,24c0,3.867-3.133,7-7,7s-7-3.133-7-7s3.133-7,7-7S31,20.133,31,24z"
              />
            </svg>
          )}
        />
      );

    default:
      return <></>;
  }
};

// Set default props
PlatformIcon.defaultProps = {
  size: 24,
  grayscale: false,
};

// ===========================================================================
// PROJECT HUB ICON
// ===========================================================================

export const ProjectsHubIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 21 19">
    <path
      d="M19.2002 5.54395C19.2002 2.9558 17.0783 0.849609 14.4521 0.849609C12.7251 0.849707 11.2189 1.7642 10.3877 3.12793C10.3106 3.25449 10.1726 3.33202 10.0244 3.33203C9.87635 3.3319 9.7392 3.25436 9.66211 3.12793C8.83087 1.76463 7.3246 0.849776 5.59766 0.849609C2.97134 0.850069 0.849609 2.95586 0.849609 5.54395C0.849628 6.13375 0.965019 6.69758 1.16797 7.2207L1.17285 7.2334V7.23438C1.9905 9.56143 4.23206 12.053 6.32812 13.9941C7.3673 14.9565 8.35466 15.769 9.08203 16.3408C9.44554 16.6266 9.74437 16.8523 9.95117 17.0059C9.97695 17.025 10.0015 17.0427 10.0244 17.0596C10.0475 17.0426 10.0726 17.0252 10.0986 17.0059C10.3054 16.8523 10.6043 16.6266 10.9678 16.3408C11.6951 15.769 12.6816 14.9563 13.7207 13.9941C15.8169 12.0531 18.059 9.5615 18.877 7.23438L18.8818 7.2207L18.9541 7.02344C19.1121 6.55697 19.2002 6.06045 19.2002 5.54395ZM20.0498 5.54395C20.0498 6.24555 19.9133 6.9135 19.6748 7.52832L19.6738 7.52734C18.7775 10.0648 16.3982 12.6741 14.2988 14.6182C13.2388 15.5997 12.2334 16.4268 11.4932 17.0088C11.1229 17.2999 10.8178 17.5298 10.6055 17.6875C10.4993 17.7663 10.4163 17.8279 10.3594 17.8691C10.3309 17.8897 10.3089 17.9053 10.2939 17.916C10.2865 17.9213 10.2803 17.9249 10.2764 17.9277L10.2715 17.9326H10.2705C10.1418 18.0238 9.97516 18.035 9.83691 17.9668L9.7793 17.9326L9.77734 17.9316C9.77628 17.9309 9.77443 17.9291 9.77246 17.9277C9.76858 17.925 9.76299 17.9211 9.75586 17.916C9.74096 17.9053 9.7188 17.8897 9.69043 17.8691C9.63349 17.8279 9.55053 17.7663 9.44434 17.6875C9.23197 17.5298 8.92696 17.2999 8.55664 17.0088C7.81642 16.4268 6.81096 15.5998 5.75098 14.6182C3.64836 12.671 1.26385 10.0569 0.371094 7.51562L0.37207 7.51465C0.136714 6.90363 1.82771e-05 6.24084 0 5.54395C0 2.47817 2.50968 0.000770142 5.59668 0L5.59766 0C7.40255 0.000148175 9.00209 0.85014 10.0244 2.16309C11.0468 0.849518 12.647 8.6992e-05 14.4521 0C17.5395 0 20.0498 2.47809 20.0498 5.54395Z"
      fill={color}
    />
  </SimpleIconSvg>
);

// ===========================================================================
// SYSTEM ICONS
// ===========================================================================

export const SettingIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 19 20">
    <path
      d="M7.64941 1.24316C8.7255 2.4971 10.5035 2.49416 11.5762 1.24316L12.1621 0.557617C12.8815 0.78597 13.5718 1.10624 14.2188 1.51074L14.1758 2.40527C14.0958 4.05358 15.3213 5.37866 16.9424 5.29199H16.9434L17.6514 5.25391C18.0357 5.99336 18.3268 6.78242 18.5195 7.60156L18.0137 8.08398C16.8345 9.20547 16.8379 11.0345 18.0137 12.1523L18.4629 12.5781C18.2443 13.4191 17.9206 14.2248 17.5 14.9746L16.9434 14.9443C15.3173 14.8569 14.0963 16.188 14.1758 17.8311L14.2051 18.4463C13.5033 18.8833 12.7512 19.2209 11.9668 19.4502L11.5762 18.9941C10.5001 17.74 8.72216 17.743 7.64941 18.9941L7.20508 19.5107C6.44075 19.3083 5.70375 19.0048 5.01172 18.6055L5.0498 17.8311C5.12982 16.183 3.9035 14.8574 2.28223 14.9443L1.45996 14.9883C1.07033 14.2966 0.763072 13.5568 0.544922 12.7852L1.21094 12.1523C2.39079 11.0317 2.38768 9.2019 1.21094 8.08398L0.483398 7.3916C0.676574 6.64194 0.952824 5.91933 1.30762 5.23926L2.28223 5.29199C3.80655 5.37398 4.97411 4.20944 5.04883 2.70996L5.0498 2.40527L4.99805 1.35156C5.63352 0.983551 6.30734 0.694578 7.00586 0.492188L7.64941 1.24316Z"
      stroke={color}
      strokeWidth="0.87"
    />
  </SimpleIconSvg>
);

export const DiagnosticsIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.60133 0.136293C6.62071 -0.0708569 7.67423 -0.0416099 8.68043 0.222231H8.68238C11.409 0.92193 13.4804 3.25813 13.8445 6.03082C13.9812 7.07702 13.8789 8.14074 13.5466 9.14215L13.489 9.31793L13.6189 9.44879L18.613 14.4673C19.0693 14.928 19.3256 15.5514 19.325 16.1998C19.3242 16.7672 19.1269 17.3147 18.7712 17.7506L18.6091 17.9302L17.9295 18.6099C17.7019 18.8373 17.4317 19.0181 17.1345 19.1412C16.8681 19.2514 16.5842 19.3102 16.2966 19.3218H16.197C15.5723 19.3218 14.9443 19.0857 14.4685 18.6099L9.44801 13.6148L9.31715 13.4839L9.14234 13.5425C8.14589 13.8731 7.08124 13.9737 6.02516 13.8394C3.33925 13.4869 1.06375 11.5277 0.287852 8.92633L0.217539 8.67243C-0.0413138 7.66825 -0.0697929 6.61762 0.135508 5.60114C0.169461 5.43087 0.249408 5.2718 0.365977 5.14313C0.482683 5.01441 0.632329 4.91917 0.798594 4.86872C0.962811 4.81886 1.13791 4.81457 1.30445 4.85602C1.47083 4.89745 1.62326 4.98322 1.74488 5.10407L4.63746 7.99664H8.00172V4.63239L5.10914 1.73981C4.98753 1.61795 4.90074 1.46547 4.85914 1.2984C4.81764 1.13147 4.8226 0.956091 4.87281 0.791567C4.92319 0.626943 5.01739 0.478113 5.14527 0.362856C5.27318 0.24768 5.43044 0.169284 5.59938 0.136293H5.60133ZM8.45582 1.09625C7.74202 0.909051 6.99922 0.857292 6.26637 0.94391L5.65211 1.01715L6.08961 1.45368L8.90211 4.26618V7.95172C8.90211 8.47595 8.47611 8.90177 7.95191 8.90192H4.26637L1.01539 5.65094L0.944102 6.26715C0.859897 6.99342 0.906976 7.72627 1.09645 8.4527C1.70169 10.8251 3.72993 12.6336 6.14723 12.9498C7.22005 13.0918 8.2681 12.949 9.25563 12.5445L9.53102 12.4302L15.1052 17.9752L15.2234 18.0806C15.8327 18.5731 16.7286 18.5392 17.2937 17.9742L17.9744 17.2935C18.2637 17.0031 18.4265 16.6097 18.4265 16.1998C18.4265 15.7896 18.2639 15.3955 17.9744 15.105L12.4295 9.53082L12.5437 9.25641L12.5447 9.25543C12.9493 8.26725 13.0855 7.21978 12.949 6.15387C12.6328 3.73663 10.8284 1.7065 8.45582 1.09625Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const ManagementIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 17 20">
    <path
      d="M8.43457 10.248C9.54197 10.248 10.639 10.466 11.6621 10.8896C12.6853 11.3135 13.6162 11.9347 14.3994 12.7178C15.1826 13.5009 15.8036 14.4318 16.2275 15.4551C16.6513 16.4783 16.8691 17.5751 16.8691 18.6826C16.8691 18.9229 16.6748 19.1172 16.4346 19.1172C16.1945 19.1169 16 18.9227 16 18.6826C16 17.6894 15.8039 16.7057 15.4238 15.7881C15.0437 14.8704 14.4866 14.0354 13.7842 13.333C13.0818 12.6307 12.2468 12.0735 11.3291 11.6934C10.4115 11.3134 9.42769 11.1172 8.43457 11.1172C7.44144 11.1172 6.4576 11.3134 5.54004 11.6934C4.62234 12.0735 3.78735 12.6306 3.08496 13.333C2.38255 14.0355 1.82547 14.8703 1.44531 15.7881C1.06528 16.7057 0.869169 17.6894 0.869141 18.6826C0.869141 18.9229 0.674814 19.1172 0.43457 19.1172C0.19454 19.1169 0 18.9227 0 18.6826C2.8627e-05 17.5751 0.217827 16.4783 0.641602 15.4551C1.06548 14.4318 1.68653 13.501 2.46973 12.7178C3.25291 11.9346 4.18377 11.3135 5.20703 10.8896C6.23017 10.4659 7.32716 10.2481 8.43457 10.248ZM8.45215 0C11.049 6.57117e-05 13.1542 2.10525 13.1543 4.70215C13.1543 7.2991 11.0491 9.40423 8.45215 9.4043C5.85521 9.40423 3.75 7.2991 3.75 4.70215C3.75007 2.10525 5.85525 6.57123e-05 8.45215 0ZM8.45215 0.870117C6.33574 0.870183 4.62018 2.58574 4.62012 4.70215C4.62012 6.81861 6.3357 8.53411 8.45215 8.53418C10.5686 8.53411 12.2842 6.81861 12.2842 4.70215C12.2841 2.58574 10.5686 0.870183 8.45215 0.870117Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const AdminIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 40 40">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.9814 10C15.2215 10 15.4168 10.1945 15.417 10.4346V12.0264H16.5C17.6046 12.0264 18.5 12.9218 18.5 14.0264V17.0264C18.5 18.062 17.7128 18.9134 16.7041 19.0156L16.5 19.0264H15.417V29.5908C15.417 29.8311 15.2217 30.0264 14.9814 30.0264C14.7414 30.0261 14.5469 29.8309 14.5469 29.5908V19.0264H13.5L13.2959 19.0156C12.3543 18.9202 11.6062 18.172 11.5107 17.2305L11.5 17.0264V14.0264C11.5 12.9218 12.3954 12.0264 13.5 12.0264H14.5469V10.4346C14.5471 10.1947 14.7415 10.0002 14.9814 10ZM13.5 12.8965C12.8759 12.8965 12.3701 13.4023 12.3701 14.0264V17.0264C12.3701 17.6504 12.8759 18.1562 13.5 18.1562H16.5C17.1241 18.1562 17.6299 17.6504 17.6299 17.0264V14.0264C17.6299 13.4023 17.1241 12.8965 16.5 12.8965H13.5Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M25.0186 9.97363C25.2585 9.97393 25.4531 10.1691 25.4531 10.4092V20.9736H26.5L26.7041 20.9844C27.6456 21.0799 28.3938 21.828 28.4893 22.7695L28.5 22.9736V25.9736C28.5 27.0782 27.6045 27.9736 26.5 27.9736H25.4531V29.5654C25.4529 29.8053 25.2584 29.9997 25.0186 30C24.7785 30 24.5832 29.8055 24.583 29.5654V27.9736H23.5C22.3954 27.9736 21.5 27.0782 21.5 25.9736V22.9736C21.5 21.938 22.2872 21.0866 23.2959 20.9844L23.5 20.9736H24.583V10.4092C24.583 10.1689 24.7783 9.97363 25.0186 9.97363ZM23.5 21.8438C22.8759 21.8438 22.3701 22.3496 22.3701 22.9736V25.9736C22.3701 26.5977 22.8759 27.1035 23.5 27.1035H26.5C27.124 27.1035 27.6299 26.5977 27.6299 25.9736V22.9736C27.6299 22.3496 27.124 21.8438 26.5 21.8438H23.5Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const GrammarOfUseIcon: FC<IconProps> = ({ size = 24 }) => (
  <SimpleIconSvg size={size} viewBox="0 0 15 20">
    <g opacity="0.5">
      <path
        d="M9.6709 0C11.6121 6.09228e-07 13.1176 0.280913 14.5996 0.898438V2.35938C12.9871 1.62408 11.4387 1.24518 9.75977 1.24512C7.35474 1.24512 5.29156 2.14933 3.68555 3.93457C2.19715 5.58879 1.4385 7.56923 1.43848 9.80273C1.43848 12.2285 2.26481 14.3374 3.92285 16.0225L3.92773 16.0273L4.23535 16.3232C5.79439 17.7556 7.67988 18.4853 9.81738 18.4854C10.6428 18.4854 11.2001 18.5453 12.3105 18.3096L12.6768 18.2314L13.5625 19.1875C12.3339 19.5113 11.0461 19.5996 9.39258 19.5996C6.83249 19.5996 4.6521 18.6715 2.77344 16.7451L2.73242 16.7021C1.09571 15.0065 0.205725 13.0205 0.0263672 10.6914L0 10.2207V9.37598C0.0925887 6.78205 1.01084 4.62597 2.79297 2.83301C4.67625 0.938759 6.93776 9.32777e-05 9.6709 0Z"
        fill="url(#paint0_linear_icons_grammarOfUse)"
      />
      <path
        d="M14.5996 10.9463V17.9619L13.5479 16.8252V12.2188H9.38379L8.27734 10.9463H14.5996Z"
        fill="url(#paint1_linear_icons_grammarOfUse)"
      />
    </g>
    <defs>
      <linearGradient
        id="paint0_linear_icons_grammarOfUse"
        x1="8.31756"
        y1="-4.06277"
        x2="-3.22704"
        y2="0.031722"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F2EBD8" />
        <stop offset="0.197115" stopColor="#ECE4D5" />
        <stop offset="1" stopColor="#C6BDAF" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_icons_grammarOfUse"
        x1="8.31756"
        y1="-4.06277"
        x2="-3.22704"
        y2="0.031722"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F2EBD8" />
        <stop offset="0.197115" stopColor="#ECE4D5" />
        <stop offset="1" stopColor="#C6BDAF" />
      </linearGradient>
    </defs>
  </SimpleIconSvg>
);

export const ContactIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 15">
    <path
      d="M18 0C19.1046 3.05989e-07 20 0.895431 20 2V13C20 14.1046 19.1046 15 18 15H2L1.7959 14.9893C0.854347 14.8938 0.1062 14.1457 0.0107422 13.2041L0 13V2L0.0107422 1.7959C0.1062 0.854346 0.854346 0.1062 1.7959 0.0107422L2 0H18ZM8.99316 6.95605C9.56718 7.46629 10.4328 7.46629 11.0068 6.95605L17.8535 0.870117H2.14648L8.99316 6.95605ZM0.870117 13C0.870118 13.6241 1.37592 14.1299 2 14.1299H18C18.6241 14.1299 19.1299 13.6241 19.1299 13V2C19.1299 1.72179 19.029 1.46738 18.8623 1.27051L11.6514 7.68164C10.7098 8.51856 9.29016 8.51856 8.34863 7.68164L1.13672 1.27051C0.970255 1.46731 0.870117 1.72201 0.870117 2V13Z"
      fill={color}
    />
  </SimpleIconSvg>
);

export const LanguageIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 21 19">
    <path
      d="M0.434998 2.68457H13.935M7.185 0.43457V2.68457M10.935 18.4346L15.435 7.93457L19.935 18.4346M12.3178 15.4346H18.5522M11.3709 2.68457C11.3709 2.68457 10.2319 7.09082 7.51312 10.4189C4.79437 13.7471 1.935 15.4346 1.935 15.4346"
      stroke={color}
      strokeWidth="0.87"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.185 13.1846C10.185 13.1846 8.54437 11.9189 6.81 9.66895C5.07562 7.41895 4.185 5.68457 4.185 5.68457"
      stroke={color}
      strokeWidth="0.87"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SimpleIconSvg>
);

export const ReportBugIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 18">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.9955 0.000381735C13.0757 -0.00234322 13.1557 0.0152783 13.2262 0.0504335L13.3 0.0964811C13.3452 0.131321 13.3822 0.173821 13.4089 0.22061L13.4408 0.293685C13.4731 0.393433 13.4621 0.502405 13.4089 0.596999L13.322 0.750157L13.323 0.751158L12.3545 2.47294L12.2636 2.63311L12.4024 2.75323C13.0545 3.32154 13.4597 4.13046 13.4598 5.02158V6.95157L13.5637 7.01864C14.1364 7.38614 14.643 7.84036 15.0614 8.35903L15.1822 8.50818L15.349 8.41208L17.974 6.89051C18.1902 6.7652 18.4694 6.83396 18.5951 7.02765L18.632 7.09973C18.6989 7.27231 18.6336 7.47412 18.4513 7.58022L15.6395 9.20891L15.7364 9.4001C16.0472 10.0106 16.2508 10.679 16.3265 11.3812L16.3485 11.5824H19.5537C19.6756 11.5824 19.7916 11.6285 19.8732 11.7065L19.8742 11.7075C19.9566 11.7854 20.0004 11.8902 20 11.9928C20 12.2063 19.8122 12.4022 19.5537 12.4022H16.3485L16.3265 12.6034C16.2623 13.2003 16.1058 13.7715 15.8692 14.3042L15.7823 14.4994L15.972 14.5965L18.676 15.9889L18.7489 16.036C18.7933 16.0708 18.8295 16.1133 18.8557 16.1601L18.8877 16.2342C18.9185 16.3348 18.9065 16.4432 18.8527 16.5365L18.8517 16.5375C18.7981 16.6317 18.7023 16.7056 18.5861 16.7377C18.4695 16.7703 18.344 16.7579 18.2386 16.7037L15.5337 15.3102L15.362 15.2221L15.2491 15.3783C14.104 16.9568 12.1805 17.9999 10.002 18C7.93966 18 6.10347 17.066 4.94159 15.6265L4.82077 15.4764L4.65502 15.5735L2.02896 17.0941C1.81262 17.2195 1.53353 17.1509 1.40789 16.9579C1.28783 16.7723 1.34359 16.5257 1.55267 16.4043L4.36246 14.7757L4.2656 14.5855C3.9539 13.9749 3.7491 13.3063 3.67349 12.6034L3.65152 12.4022H0.446333C0.187759 12.4022 2.10808e-06 12.2053 2.10808e-06 11.9918C-0.000351451 11.8892 0.0437769 11.7855 0.125813 11.7075C0.208569 11.6289 0.3245 11.5824 0.446333 11.5824H3.65152L3.67349 11.3812C3.73777 10.7842 3.89412 10.212 4.13081 9.67939L4.21767 9.48419L4.02796 9.38709L1.32501 7.99465C1.21964 7.94023 1.14546 7.8511 1.11433 7.7504C1.08349 7.65014 1.09635 7.54103 1.15028 7.44809C1.26467 7.25081 1.53698 7.16739 1.76136 7.28091L4.4673 8.67335L4.63904 8.76144L4.75187 8.60528C5.20353 7.9821 5.77767 7.44128 6.44034 7.01664L6.54418 6.95057V5.02057C6.5444 4.13016 6.94948 3.32041 7.6006 2.75223L7.73839 2.6321L6.59511 0.596999L6.59411 0.595998L6.56216 0.522922C6.53834 0.448367 6.53868 0.368046 6.56316 0.292684C6.59522 0.193209 6.6705 0.103992 6.77683 0.0504335L6.77584 0.0494325C6.99597 -0.0600247 7.25876 0.0207367 7.37594 0.208597L7.38392 0.222612L8.4004 2.02648L8.49326 2.19065L8.67099 2.12758C9.02902 2.00085 9.41615 1.93038 9.82027 1.93038H10.1797C10.5834 1.93043 10.9699 1.9994 11.327 2.12658L11.364 2.13959H11.5347L11.5986 2.02548L12.6141 0.221611C12.6847 0.0961155 12.8245 0.00876718 12.9865 0.000381735H12.9955ZM9.78532 6.80142L9.78432 6.80942C9.7319 6.81142 9.67958 6.81202 9.62756 6.81543L9.26011 6.85147C9.07619 6.8744 8.89738 6.91256 8.72691 6.95257C8.70393 6.9575 8.68138 6.96214 8.65102 6.96759C8.62279 6.97266 8.58819 6.97869 8.55317 6.98761L8.55217 6.98861C8.08949 7.10973 7.64263 7.28818 7.22516 7.52116L7.17723 7.54318L7.03345 7.59924V7.62927C6.33059 8.05917 5.73888 8.63827 5.30704 9.32202L5.29905 9.31502L5.15826 9.56828C4.75815 10.2902 4.53116 11.1142 4.53021 11.9888L4.54319 12.3492C4.60354 13.181 4.86939 13.9591 5.29206 14.6365L5.30005 14.6495L5.31103 14.6625C5.32116 14.6745 5.33138 14.6887 5.34199 14.7046C5.3493 14.7156 5.35793 14.7301 5.36795 14.7496L5.37394 14.7617L5.38093 14.7727C6.35188 16.2239 8.05965 17.1791 10.002 17.1791C12.048 17.179 13.8301 16.1186 14.7678 14.5354L14.7748 14.5244L14.7798 14.5124C14.785 14.5009 14.7927 14.488 14.8008 14.4744L14.8018 14.4713C14.8105 14.4562 14.8184 14.4442 14.8248 14.4353L14.8677 14.3763V14.3562C15.25 13.6473 15.4669 12.8427 15.4678 11.9888C15.4677 11.0195 15.189 10.1124 14.7049 9.33804L14.698 9.32503L14.689 9.31402L14.662 9.27698L14.66 9.27497L14.638 9.23593L14.6321 9.22392L14.6251 9.21291L14.4493 8.96565C14.0228 8.40017 13.479 7.92012 12.8527 7.5572L12.8387 7.54919L12.8248 7.54318L12.7788 7.52216L12.7279 7.49413H12.7239C12.4271 7.33229 12.1163 7.19662 11.7953 7.09072L11.4508 6.98861C11.4124 6.97849 11.3741 6.97196 11.344 6.96659C11.3113 6.96075 11.2868 6.95577 11.2641 6.95057C11.0995 6.91248 10.9233 6.87261 10.7419 6.84947C10.5011 6.81886 10.2541 6.80143 10.002 6.80142H9.78532ZM9.82027 2.74923L9.81927 2.75023C9.37845 2.75073 8.96596 2.86225 8.61208 3.05754L8.5991 3.06355L8.58812 3.07256C8.56991 3.08583 8.5499 3.09778 8.52921 3.1076L8.48727 3.12762L8.46131 3.15464C7.84185 3.56018 7.43897 4.24288 7.43884 5.02858V6.4941L7.74039 6.38599C8.44353 6.13185 9.20573 5.99164 10.002 5.99158C10.7986 5.99158 11.5612 6.13059 12.2636 6.38398L12.5642 6.4931V5.02758C12.564 4.23735 12.1562 3.55231 11.5307 3.14764L11.5067 3.12361V3.12061L11.3899 3.05654C11.036 2.86067 10.6226 2.74923 10.1797 2.74923H9.82027Z"
      fill={color}
    />
  </SimpleIconSvg>
);

// ===========================================================================
// WRITE / FORM ICON
// ===========================================================================

const FormWriteSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 0.375C6.13261 0.375 6.25975 0.427716 6.35352 0.521484C6.44728 0.615253 6.5 0.742392 6.5 0.875C6.5 1.00761 6.44728 1.13475 6.35352 1.22852C6.25975 1.32228 6.13261 1.375 6 1.375H1V11.375H11V6.375C11 6.24239 11.0527 6.11525 11.1465 6.02148C11.2403 5.92772 11.3674 5.875 11.5 5.875C11.6326 5.875 11.7597 5.92772 11.8535 6.02148C11.9473 6.11525 12 6.24239 12 6.375V11.875C12 12.0076 11.9473 12.1347 11.8535 12.2285C11.7597 12.3223 11.6326 12.375 11.5 12.375H0.5C0.367392 12.375 0.240253 12.3223 0.146484 12.2285C0.0527163 12.1347 0 12.0076 0 11.875V0.875C0 0.742392 0.0527162 0.615253 0.146484 0.521484C0.240253 0.427716 0.367392 0.375 0.5 0.375H6Z"
      fill="currentColor"
    />
    <path
      d="M4.07812 5.05762C3.87805 5.25704 3.74766 5.51622 3.70801 5.7959L3.47363 7.44531C3.44516 7.6449 3.46364 7.84875 3.52734 8.04004C3.59114 8.2314 3.69919 8.40617 3.8418 8.54883C3.98435 8.69127 4.15845 8.79852 4.34961 8.8623C4.54092 8.92609 4.74469 8.94445 4.94434 8.91602L6.59473 8.67773C6.87203 8.63824 7.12944 8.51094 7.32812 8.31348L10 5.6416V10.2969H2V2.29688H6.84082L4.07812 5.05762Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.8867 0C11.2842 0 11.666 0.157573 11.9473 0.438477C12.0865 0.577697 12.1971 0.741925 12.2725 0.923828C12.3479 1.10582 12.3867 1.30105 12.3867 1.49805C12.3867 1.69506 12.3479 1.89025 12.2725 2.07227C12.197 2.25431 12.0866 2.42027 11.9473 2.55957L10 4.50684V4.51074L6.76465 7.74609L6.70312 7.79785C6.68446 7.81123 6.66286 7.8194 6.64258 7.83008C6.63703 7.83303 6.63163 7.83612 6.62598 7.83887C6.60714 7.84789 6.58823 7.85568 6.56836 7.8623C6.55201 7.86784 6.53547 7.87215 6.51855 7.87598C6.50602 7.87875 6.49421 7.88492 6.48145 7.88672L4.83105 8.12402C4.75442 8.13486 4.67597 8.128 4.60254 8.10352L4.60059 8.10254C4.52698 8.07799 4.45916 8.03631 4.4043 7.98145C4.34949 7.92659 4.30773 7.85969 4.2832 7.78613C4.25874 7.71261 4.25176 7.63433 4.2627 7.55762L4.49707 5.90723C4.51232 5.79962 4.56265 5.69976 4.63965 5.62305L9.82715 0.438477C10.1083 0.15775 10.4894 8.66449e-05 10.8867 0ZM10.8896 0.982422C10.822 0.984107 10.7549 0.999151 10.6934 1.02734C10.6319 1.05555 10.5767 1.09635 10.5312 1.14648L9.37988 2.29688H9.38379L5.46582 6.21289L5.34863 7.03613L6.17188 6.91992L11.2383 1.85254C11.2883 1.80713 11.3283 1.75183 11.3564 1.69043C11.3846 1.62901 11.4006 1.56265 11.4023 1.49512C11.404 1.42743 11.3913 1.35975 11.3662 1.29688C11.3411 1.23405 11.3037 1.17677 11.2559 1.12891C11.208 1.08108 11.1507 1.0437 11.0879 1.01855C11.025 0.993407 10.9574 0.980755 10.8896 0.982422Z"
      fill="currentColor"
    />
  </svg>
);

export const FormWriteIcon: FC<IconProps> = ({ size, color, style }) => (
  <Icon
    aria-label="form-write"
    component={FormWriteSvg}
    style={{ color, ...(size === undefined ? {} : { fontSize: size }), ...style }}
  />
);

const TranslationSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.20312 3.10156H14.2031M8.20312 1.10156V3.10156M11.5365 17.1016L15.5365 7.76823L19.5365 17.1016M12.7656 14.4349H18.3073M11.924 3.10156C11.924 3.10156 10.9115 7.01823 8.49479 9.97656C6.07813 12.9349 4.10156 13.5 4.10156 13.5M10.8646 12.4362C10.8646 12.4362 9.40625 11.3112 7.86458 9.3112C6.32292 7.3112 5.53125 5.76953 5.53125 5.76953"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M0.452148 13.5566C0.520001 13.5577 0.586529 13.5759 0.645508 13.6104H0.646484L0.648438 13.6113L0.651367 13.6133L2.41113 14.6631C2.59444 14.7733 2.65831 15.0142 2.54395 15.2061L2.54492 15.207L2.54004 15.2168L2.49902 15.1865L2.53906 15.2168L2.49512 15.2754C2.47778 15.2944 2.45781 15.3115 2.43652 15.3271C2.39411 15.3583 2.3461 15.3803 2.2959 15.3926C2.27066 15.3987 2.24476 15.4022 2.21875 15.4033L2.14062 15.3994C2.08931 15.3913 2.03894 15.3733 1.99414 15.3457L1.98633 15.3398V15.3389L0.831055 14.6465L1.02051 15.4531C1.36087 16.9033 2.1815 18.1964 3.34863 19.1221C4.5158 20.0477 5.96156 20.5526 7.45117 20.5537H7.45605L7.52539 20.5605L7.53125 20.5615C7.71075 20.5998 7.85059 20.7633 7.85059 20.9531C7.85058 21.1698 7.66787 21.3525 7.45117 21.3525C3.37448 21.3525 0.0517939 18.0288 0.0517578 13.9521C0.0515779 13.8826 0.0690908 13.8129 0.104492 13.752C0.139794 13.6913 0.191298 13.6408 0.251953 13.6064C0.313118 13.572 0.382731 13.5557 0.452148 13.5566ZM14.4512 0.0517578C18.5284 0.0617075 21.8515 3.3866 21.8516 7.46289C21.8517 7.5329 21.833 7.60257 21.7979 7.66309C21.7626 7.72356 21.711 7.77426 21.6504 7.80859C21.5898 7.84264 21.5201 7.86034 21.4502 7.85938C21.3805 7.85824 21.3124 7.83846 21.2529 7.80273L21.2422 7.7959H21.2412L19.4971 6.74902C19.313 6.64099 19.2472 6.39924 19.3643 6.19824C19.4466 6.05729 19.6073 5.98527 19.7686 6.01172L19.8379 6.0293L19.8418 6.03027L19.9102 6.06152L19.916 6.06445H19.915L21.0713 6.75781L20.8818 5.95215C20.2172 3.12992 17.7403 0.989575 14.7432 0.858398L14.4512 0.851562C14.2348 0.851336 14.052 0.668526 14.0518 0.452148C14.0518 0.26237 14.1919 0.0989305 14.3711 0.0605469L14.376 0.0595703L14.4453 0.0517578L14.4512 0.0507812V0.0517578Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={0.75}
      strokeLinejoin="round"
    />
  </svg>
);

export const TranslationIcon: FC<IconProps> = ({ size, color, style }) => (
  <Icon
    aria-label="translation"
    component={TranslationSvg}
    style={{ color, ...(size === undefined ? {} : { fontSize: size }), ...style }}
  />
);

// ===========================================================================
// FAVORITE / HEART ICON
// ===========================================================================

const HeartOutlineSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.2002 5.54395C19.2002 2.9558 17.0783 0.849609 14.4521 0.849609C12.7251 0.849707 11.2189 1.7642 10.3877 3.12793C10.3106 3.25449 10.1726 3.33202 10.0244 3.33203C9.87635 3.3319 9.7392 3.25436 9.66211 3.12793C8.83087 1.76463 7.3246 0.849776 5.59766 0.849609C2.97134 0.850069 0.849609 2.95586 0.849609 5.54395C0.849628 6.13375 0.965019 6.69758 1.16797 7.2207L1.17285 7.2334V7.23438C1.9905 9.56143 4.23206 12.053 6.32812 13.9941C7.3673 14.9565 8.35466 15.769 9.08203 16.3408C9.44554 16.6266 9.74437 16.8523 9.95117 17.0059C9.97695 17.025 10.0015 17.0427 10.0244 17.0596C10.0475 17.0426 10.0726 17.0252 10.0986 17.0059C10.3054 16.8523 10.6043 16.6266 10.9678 16.3408C11.6951 15.769 12.6816 14.9563 13.7207 13.9941C15.8169 12.0531 18.059 9.5615 18.877 7.23438L18.8818 7.2207L18.9541 7.02344C19.1121 6.55697 19.2002 6.06045 19.2002 5.54395ZM20.0498 5.54395C20.0498 6.24555 19.9133 6.9135 19.6748 7.52832L19.6738 7.52734C18.7775 10.0648 16.3982 12.6741 14.2988 14.6182C13.2388 15.5997 12.2334 16.4268 11.4932 17.0088C11.1229 17.2999 10.8178 17.5298 10.6055 17.6875C10.4993 17.7663 10.4163 17.8279 10.3594 17.8691C10.3309 17.8897 10.3089 17.9053 10.2939 17.916C10.2865 17.9213 10.2803 17.9249 10.2764 17.9277L10.2715 17.9326H10.2705C10.1418 18.0238 9.97516 18.035 9.83691 17.9668L9.7793 17.9326L9.77734 17.9316C9.77628 17.9309 9.77443 17.9291 9.77246 17.9277C9.76858 17.925 9.76299 17.9211 9.75586 17.916C9.74096 17.9053 9.7188 17.8897 9.69043 17.8691C9.63349 17.8279 9.55053 17.7663 9.44434 17.6875C9.23197 17.5298 8.92696 17.2999 8.55664 17.0088C7.81642 16.4268 6.81096 15.5998 5.75098 14.6182C3.64836 12.671 1.26385 10.0569 0.371094 7.51562L0.37207 7.51465C0.136714 6.90363 1.82771e-05 6.24084 0 5.54395C0 2.47817 2.50968 0.000770142 5.59668 0L5.59766 0C7.40255 0.000148175 9.00209 0.85014 10.0244 2.16309C11.0468 0.849518 12.647 8.6992e-05 14.4521 0C17.5395 0 20.0498 2.47809 20.0498 5.54395Z"
      fill="currentColor"
    />
  </svg>
);

// Outer contour of HeartOutlineSvg traced alone (no inner cutout), producing
// a solid heart from the same artwork for the "favorited" state.
const HeartSolidSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 21 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.0498 5.54395C20.0498 6.24555 19.9133 6.9135 19.6748 7.52832L19.6738 7.52734C18.7775 10.0648 16.3982 12.6741 14.2988 14.6182C13.2388 15.5997 12.2334 16.4268 11.4932 17.0088C11.1229 17.2999 10.8178 17.5298 10.6055 17.6875C10.4993 17.7663 10.4163 17.8279 10.3594 17.8691C10.3309 17.8897 10.3089 17.9053 10.2939 17.916C10.2865 17.9213 10.2803 17.9249 10.2764 17.9277L10.2715 17.9326H10.2705C10.1418 18.0238 9.97516 18.035 9.83691 17.9668L9.7793 17.9326L9.77734 17.9316C9.77628 17.9309 9.77443 17.9291 9.77246 17.9277C9.76858 17.925 9.76299 17.9211 9.75586 17.916C9.74096 17.9053 9.7188 17.8897 9.69043 17.8691C9.63349 17.8279 9.55053 17.7663 9.44434 17.6875C9.23197 17.5298 8.92696 17.2999 8.55664 17.0088C7.81642 16.4268 6.81096 15.5998 5.75098 14.6182C3.64836 12.671 1.26385 10.0569 0.371094 7.51562L0.37207 7.51465C0.136714 6.90363 1.82771e-05 6.24084 0 5.54395C0 2.47817 2.50968 0.000770142 5.59668 0L5.59766 0C7.40255 0.000148175 9.00209 0.85014 10.0244 2.16309C11.0468 0.849518 12.647 8.6992e-05 14.4521 0C17.5395 0 20.0498 2.47809 20.0498 5.54395Z"
      fill="currentColor"
    />
  </svg>
);

export const HeartIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="favorite"
    component={HeartOutlineSvg}
    onClick={onClick}
    className={anticonClass('favorite', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

export const HeartFilledIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="favorite-filled"
    component={HeartSolidSvg}
    onClick={onClick}
    className={anticonClass('favorite-filled', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

// ===========================================================================
// COMPOSE / NEW ICON
// ===========================================================================

const ComposeSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.6344 8.21694C17.6344 7.94764 17.8442 7.72819 18.1035 7.72819C18.3619 7.72819 18.5725 7.94679 18.5725 8.21694V17.8796C18.5725 18.4631 18.3444 18.9926 17.9751 19.3774C17.6065 19.7614 17.0976 20 16.5376 20H2.0349C1.47492 20 0.966795 19.7623 0.597467 19.3774C0.228936 18.9934 0 18.4631 0 17.8796V2.76747C0 2.18396 0.228139 1.65448 0.597467 1.26964C0.965998 0.88562 1.47492 0.647064 2.0349 0.647064H12.3106C12.569 0.647064 12.7796 0.86566 12.7796 1.13581C12.7796 1.40512 12.5698 1.62457 12.3106 1.62457H2.0349C1.73416 1.62457 1.45977 1.7534 1.26034 1.96036C1.06092 2.16816 0.937287 2.45326 0.937287 2.76747V17.8796C0.937287 18.193 1.06093 18.4789 1.26034 18.6867C1.45976 18.8945 1.73337 19.0225 2.0349 19.0225H16.5376C16.8384 19.0225 17.1128 18.8937 17.3122 18.6867C17.5116 18.4789 17.6352 18.1938 17.6352 17.8796V8.21694H17.6344ZM16.9684 0.142738L19.8624 3.15835C20.0459 3.3487 20.0459 3.65792 19.8624 3.84908L10.3548 13.7562C10.2558 13.8593 10.1234 13.9067 9.99341 13.8975L7.0802 13.9574C6.82256 13.9624 6.60877 13.7496 6.60399 13.4803L6.66223 10.3857C6.66462 10.2552 6.71647 10.1363 6.79863 10.0516L6.79703 10.0499L16.3047 0.142758C16.4881 -0.0475861 16.7841 -0.0475861 16.9676 0.142758L16.9684 0.142738ZM18.8685 3.50413L16.6373 1.17922L7.59321 10.6033L7.54854 12.9749L9.82442 12.9283L18.8685 3.50413Z"
      fill="currentColor"
    />
  </svg>
);

export const ComposeIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="compose"
    component={ComposeSvg}
    onClick={onClick}
    className={anticonClass('compose', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

// ===========================================================================
// ACTION ICONS (save / publish / delete / copy / duplicate / move / select / edit / pin)
// ===========================================================================

const SaveSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.6666 0L20 4.3332V19.5906C19.9999 19.699 19.9563 19.803 19.8797 19.8797C19.803 19.9562 19.699 19.9999 19.5906 20H0.4084C0.3002 19.9998 0.1969 19.956 0.1203 19.8797C0.0437 19.803 0.0001 19.699 0 19.5906V0.4094C0.0001 0.3009 0.0436 0.1971 0.1203 0.1203C0.1969 0.0439 0.3002 0.0002 0.4084 0H15.6666ZM0.8178 0.8187V19.1813H4.7951V11.9876H15.2049V19.1813H19.1813V4.6715L15.3292 0.8187H0.8178ZM5.6138 12.8073V19.1813H14.3862V12.8073H5.6138Z"
      fill="currentColor"
    />
  </svg>
);

export const SaveIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="save"
    component={SaveSvg}
    onClick={onClick}
    className={anticonClass('save', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const PublishSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 21.04 20.12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.0728 0.0107C20.3168 0.0606 20.4998 0.2758 20.4998 0.5341V10.5595C20.4998 10.7998 20.3058 10.9941 20.0648 10.9941C19.8248 10.9941 19.6308 10.7998 19.6308 10.5595V4.5888L0.8692 4.5878V19.249L14.4502 19.2402C14.6905 19.24 14.8856 19.4345 14.8858 19.6748C14.8858 19.9148 14.6913 20.1091 14.4512 20.1093L0.5352 20.1201C0.2397 20.1203 0.0003 19.8813 0 19.5859V0.5351C0.0002 0.2399 0.239 0.0011 0.5342 0.0009L19.9658 0L20.0728 0.0107ZM0.8692 0.8691V3.7187L19.6308 3.7197V0.8691H0.8692Z"
      fill="currentColor"
    />
    <path
      d="M16.5837 10.1796C16.7125 10.0511 16.9022 10.0203 17.0603 10.0869C17.1373 10.1044 17.2111 10.1422 17.2712 10.2021L20.8728 14.038C21.0418 14.2077 21.0418 14.4825 20.8728 14.6523C20.7028 14.8215 20.4268 14.8225 20.2578 14.6533L17.3757 11.5371V19.625C17.3754 19.8647 17.1808 20.0591 16.9411 20.0595C16.7014 20.0591 16.5058 19.8647 16.5056 19.625V11.4882L13.2312 14.6533C13.0615 14.8222 12.7866 14.8221 12.6169 14.6533C12.4474 14.4836 12.4468 14.2079 12.6159 14.038L16.5837 10.1796Z"
      fill="currentColor"
    />
  </svg>
);

export const PublishIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="publish"
    component={PublishSvg}
    onClick={onClick}
    className={anticonClass('publish', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const DeleteSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.566 3.19C17.681 3.19 17.791 3.2355 17.873 3.3162C17.954 3.3971 18 3.507 18 3.621C18 3.7349 17.954 3.8441 17.873 3.9248C17.791 4.0056 17.681 4.0511 17.566 4.0511H15.885L15.88 4.1478L15.858 4.6577L15.856 4.7139L14.858 18.6122C14.831 18.9886 14.661 19.3411 14.383 19.5985C14.104 19.856 13.738 19.9999 13.357 20H4.642C4.261 19.9999 3.895 19.856 3.616 19.5985C3.403 19.4013 3.253 19.1484 3.183 18.8716L3.158 18.7522L3.141 18.6122L2.143 4.7149L2.142 4.6666L2.127 4.1497L2.124 4.0511H0.434C0.319 4.0511 0.209 4.0056 0.127 3.9248C0.046 3.8441 0 3.7349 0 3.621C0 3.507 0.046 3.3971 0.127 3.3162C0.209 3.2355 0.319 3.19 0.434 3.19H17.566ZM2.966 4.0511L4.045 19.0442L4.052 19.1389H13.948L13.955 19.0442L15.034 4.0511H2.966Z"
      fill="currentColor"
    />
    <path
      d="M11.141 0C11.256 0 11.368 0.0464 11.449 0.1272C11.53 0.208 11.575 0.3173 11.575 0.4311C11.575 0.5448 11.53 0.6541 11.449 0.7349C11.368 0.8157 11.256 0.8611 11.141 0.8611H6.859C6.744 0.8611 6.632 0.8157 6.551 0.7349C6.47 0.6541 6.425 0.5448 6.425 0.4311C6.425 0.3173 6.47 0.208 6.551 0.1272C6.632 0.0464 6.744 0 6.859 0H11.141Z"
      fill="currentColor"
    />
  </svg>
);

export const DeleteIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="delete"
    component={DeleteSvg}
    onClick={onClick}
    className={anticonClass('delete', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const CopySvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.104 0C19.155 0 20 0.8338 20 1.8541V13.8469C20 14.8672 19.155 15.701 18.104 15.701H16.033V18.1459C16.033 19.1662 15.187 20 14.136 20H1.896C0.846 20 0 19.1662 0 18.1459V6.1531C0 5.1328 0.846 4.299 1.896 4.299H3.968V1.8541C3.968 0.8338 4.814 0 5.865 0H18.104ZM1.896 5.1408C1.33 5.1408 0.866 5.5913 0.866 6.1531V18.1459C0.866 18.7077 1.33 19.1582 1.896 19.1582H14.136C14.702 19.1582 15.167 18.7077 15.167 18.1459V6.1531C15.167 5.5913 14.702 5.1408 14.136 5.1408H1.896ZM5.865 0.8418C5.299 0.8418 4.834 1.2923 4.834 1.8541V4.299H14.136C14.64 4.299 15.124 4.4953 15.479 4.8433C15.834 5.1912 16.033 5.6629 16.033 6.1531V14.8592H18.104C18.67 14.8592 19.134 14.4087 19.134 13.8469V1.8541C19.134 1.2923 18.67 0.8419 18.104 0.8418H5.865Z"
      fill="currentColor"
    />
  </svg>
);

export const CopyIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="copy"
    component={CopySvg}
    onClick={onClick}
    className={anticonClass('copy', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const DuplicateSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="0.435"
      y="0.435"
      width="14.13"
      height="19.13"
      rx="0.565"
      stroke="currentColor"
      strokeWidth="0.87"
    />
    <line
      x1="4.935"
      y1="10.0152"
      x2="10.065"
      y2="10.0152"
      stroke="currentColor"
      strokeWidth="0.87"
      strokeLinecap="round"
    />
    <line
      x1="7.485"
      y1="7.435"
      x2="7.485"
      y2="12.565"
      stroke="currentColor"
      strokeWidth="0.87"
      strokeLinecap="round"
    />
  </svg>
);

export const DuplicateIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="duplicate"
    component={DuplicateSvg}
    onClick={onClick}
    className={anticonClass('duplicate', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const MoveSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19.72 20.2" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.716 0C17.059 0 17.33 0.1644 17.564 0.3692C17.791 0.5677 18.042 0.8573 18.338 1.1963L19.447 2.5244C19.605 2.7053 19.587 2.9796 19.406 3.1377C19.225 3.2957 18.951 3.2774 18.793 3.0967L17.684 1.7686C17.461 1.5139 17.292 1.3228 17.151 1.1787L17.168 7.0435C17.309 6.8995 17.479 6.7082 17.701 6.4537L18.911 4.9834C19.069 4.8027 19.343 4.7844 19.524 4.9424C19.705 5.1005 19.723 5.3748 19.565 5.5557L18.356 7.026C18.06 7.3653 17.807 7.6545 17.58 7.8531C17.346 8.0576 17.075 8.2232 16.733 8.2232C16.391 8.2232 16.12 8.0579 15.886 7.8531C15.659 7.6546 15.408 7.3649 15.111 7.026L13.985 5.5967C13.827 5.4158 13.845 5.1415 14.026 4.9834C14.207 4.8254 14.481 4.8437 14.639 5.0244L15.765 6.4537C15.988 6.708 16.158 6.8986 16.299 7.0426L16.282 1.1778C16.141 1.3219 15.971 1.5137 15.748 1.7686L14.598 3.0967C14.44 3.2774 14.166 3.2957 13.985 3.1377C13.804 2.9796 13.786 2.7053 13.944 2.5244L15.093 1.1963C15.39 0.857 15.642 0.5678 15.869 0.3692C16.104 0.1647 16.374 0 16.716 0Z"
      fill="currentColor"
    />
    <path
      d="M17.038 12.2227C17.038 10.942 16.345 10.0556 15.313 9.4365C15.032 9.268 14.673 9.1425 14.125 9.0235C13.574 8.9039 12.867 8.7973 11.896 8.6514L11.892 8.6504L8.901 8.1729C8.69 8.139 8.535 7.9568 8.535 7.7432V2.3145C8.539 2.139 8.509 1.9641 8.444 1.8008C8.38 1.6375 8.284 1.4888 8.161 1.3633C8.039 1.2377 7.892 1.1375 7.73 1.0694C7.569 1.0012 7.394 0.9658 7.219 0.9658C7.043 0.9658 6.87 1.0013 6.708 1.0694C6.546 1.1375 6.399 1.2377 6.276 1.3633C6.154 1.4888 6.057 1.6376 5.993 1.8008C5.929 1.9641 5.898 2.139 5.902 2.3145V12.2481C5.902 12.4179 5.804 12.5725 5.649 12.6436C5.495 12.7146 5.314 12.6887 5.185 12.5781L3.05 10.749C2.802 10.5393 2.485 10.4291 2.16 10.4405C1.835 10.4518 1.527 10.5839 1.294 10.8106C1.061 11.0373 0.921 11.3427 0.901 11.667C0.882 11.9913 0.983 12.312 1.187 12.5655L4.546 16.7022C5.475 17.8456 5.943 18.4128 6.449 18.7559L6.671 18.8897L6.672 18.8906C6.783 18.951 6.896 19.0055 7.013 19.0537C7.647 19.3152 8.369 19.3233 10.052 19.3233H11.102C12.366 19.3233 12.911 19.3195 13.381 19.1846C14.177 18.9559 14.869 18.4572 15.337 17.7744C15.613 17.3721 15.789 16.8553 16.189 15.6543C16.815 13.7756 17.038 13.1637 17.038 12.2227ZM17.908 12.2227C17.908 13.0585 17.748 13.6983 17.411 14.7363L17.015 15.9297C16.631 17.0788 16.416 17.739 16.054 18.2666C15.471 19.1159 14.611 19.7361 13.621 20.0205C13.005 20.1974 12.312 20.1934 11.102 20.1934H10.052C8.441 20.1934 7.514 20.2009 6.683 19.8584C6.537 19.7981 6.393 19.7298 6.255 19.6543C5.465 19.2255 4.887 18.501 3.871 17.251L0.51 13.1113L0.508 13.1094C0.169 12.6874 0 12.1543 0.033 11.6143C0.066 11.0741 0.299 10.5652 0.687 10.1875C1.074 9.8098 1.589 9.5902 2.13 9.5713C2.671 9.5524 3.199 9.7354 3.612 10.085L3.614 10.0869L5.032 11.3018V2.3252C5.027 2.0372 5.078 1.7506 5.184 1.4824C5.29 1.2112 5.451 0.9644 5.654 0.7559C5.858 0.5472 6.101 0.3808 6.37 0.2676C6.639 0.1545 6.927 0.0967 7.219 0.0967C7.51 0.0967 7.799 0.1545 8.067 0.2676C8.336 0.3808 8.58 0.5472 8.783 0.7559C8.987 0.9645 9.147 1.2112 9.254 1.4824C9.359 1.7506 9.411 2.0372 9.405 2.3252V7.3721L12.024 7.791C12.984 7.9351 13.725 8.0459 14.31 8.1729C14.897 8.3003 15.361 8.4511 15.76 8.6905C16.973 9.4174 17.908 10.5513 17.908 12.2227Z"
      fill="currentColor"
    />
  </svg>
);

export const MoveIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="move"
    component={MoveSvg}
    onClick={onClick}
    className={anticonClass('move', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const SelectSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 17.79 20.06" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.238 1.7402C7.529 1.7402 7.818 1.798 8.087 1.9111C8.355 2.0243 8.599 2.1908 8.802 2.3994C9.006 2.6079 9.166 2.8547 9.273 3.1259C9.379 3.3941 9.43 3.6807 9.424 3.9687L9.284 8.0234L11.903 8.4423C12.862 8.5864 13.604 8.6973 14.188 8.8242C14.775 8.9516 15.239 9.1025 15.638 9.3418C16.851 10.0687 17.787 11.2026 17.787 12.874C17.787 13.7098 17.674 14.0527 17.338 15.0908L16.941 16.2841C16.558 17.4329 16.415 17.5925 16.053 18.1201C15.471 18.9693 14.611 19.5896 13.621 19.874C13.005 20.0509 12.312 20.0468 11.101 20.0468H10.051C8.44 20.0468 7.513 20.0543 6.682 19.7119C6.537 19.6516 6.393 19.5833 6.255 19.5078C5.465 19.0789 4.886 18.3543 3.871 17.1045L0.509 12.9648L0.507 12.9629C0.169 12.5409 0 12.0077 0.033 11.4677C0.066 10.9276 0.299 10.4186 0.686 10.041C1.074 9.6634 1.589 9.4437 2.13 9.4248C2.67 9.4059 3.199 9.589 3.612 9.9384L3.614 9.9404L5.032 11.1552L5.051 3.9687C5.046 3.6807 5.097 3.3941 5.203 3.1259C5.309 2.8547 5.47 2.6079 5.673 2.3994C5.877 2.1908 6.121 2.0243 6.389 1.9111C6.658 1.798 6.947 1.7402 7.238 1.7402ZM7.238 2.6093C7.063 2.6093 6.889 2.6449 6.727 2.7129C6.565 2.781 6.418 2.8812 6.296 3.0068C6.173 3.1323 6.077 3.2811 6.012 3.4443C5.948 3.6076 5.917 3.7825 5.922 3.958L5.902 12.1015C5.902 12.2713 5.803 12.426 5.649 12.497C5.495 12.568 5.313 12.5421 5.184 12.4316L3.049 10.6025C2.802 10.3929 2.484 10.2826 2.16 10.2939C1.835 10.3053 1.526 10.4374 1.294 10.664C1.061 10.8907 0.921 11.1963 0.901 11.5205C0.881 11.8447 0.983 12.1655 1.186 12.4189L4.546 16.5556C5.474 17.6989 5.942 18.2663 6.449 18.6093L6.671 18.7431L6.672 18.7441C6.782 18.8044 6.896 18.8589 7.012 18.9072C7.647 19.1687 8.369 19.1767 10.051 19.1767H11.101C12.366 19.1767 12.911 19.1729 13.38 19.038C14.176 18.8094 14.868 18.3107 15.337 17.6279C15.612 17.2257 15.715 17.2094 16.116 16.0088C16.741 14.13 16.917 13.815 16.917 12.874C16.917 11.5933 16.224 10.7069 15.191 10.0879C14.91 9.9193 14.552 9.7938 14.004 9.6748C13.453 9.5552 12.745 9.4485 11.774 9.3027L11.77 9.3017L8.78 8.8242C8.569 8.7902 8.414 8.608 8.414 8.3945L8.554 3.958C8.558 3.7825 8.528 3.6076 8.464 3.4443C8.399 3.2811 8.303 3.1323 8.18 3.0068C8.058 2.8813 7.911 2.781 7.75 2.7129C7.588 2.6447 7.413 2.6094 7.238 2.6093Z"
      fill="currentColor"
    />
    <path
      d="M7.345 0.0019C7.371 0.0027 7.397 0.0026 7.422 0.0039L7.499 0.0097C7.531 0.012 7.564 0.0154 7.596 0.0185L7.686 0.0263C7.707 0.0288 7.728 0.0323 7.75 0.0351C7.788 0.0402 7.825 0.0465 7.863 0.0527C7.905 0.0596 7.948 0.0678 7.99 0.0761C8.029 0.0838 8.068 0.0908 8.106 0.0996C8.15 0.1096 8.194 0.1212 8.238 0.1328C8.271 0.1415 8.305 0.1495 8.338 0.1591C8.58 0.2296 8.815 0.3233 9.04 0.4384C9.044 0.4409 9.05 0.4428 9.054 0.4453C9.067 0.452 9.08 0.4599 9.093 0.4668C9.15 0.4967 9.205 0.5279 9.26 0.5605C9.28 0.5722 9.299 0.5846 9.319 0.5966C9.348 0.6147 9.378 0.6316 9.407 0.6504C9.427 0.6632 9.445 0.6782 9.464 0.6914C9.497 0.7134 9.529 0.7357 9.561 0.7588C9.596 0.784 9.631 0.8096 9.665 0.8359C9.701 0.8638 9.736 0.8927 9.771 0.9218C9.806 0.9511 9.841 0.9803 9.876 1.0107C9.902 1.0343 9.928 1.0587 9.954 1.083C9.996 1.1225 10.037 1.1627 10.078 1.2041C10.097 1.224 10.116 1.2443 10.135 1.2646C10.177 1.3094 10.219 1.3547 10.258 1.4013C10.277 1.4226 10.294 1.4441 10.312 1.4658C10.346 1.507 10.379 1.5492 10.411 1.5918C10.435 1.6233 10.458 1.6551 10.481 1.6875C10.51 1.728 10.538 1.7689 10.565 1.8105C10.575 1.825 10.585 1.8389 10.594 1.8535C10.604 1.869 10.613 1.8857 10.623 1.9013C10.651 1.9476 10.679 1.9944 10.706 2.042C10.723 2.0723 10.739 2.103 10.755 2.1338C10.769 2.1586 10.783 2.1828 10.796 2.208C10.804 2.2255 10.811 2.2441 10.82 2.2617C10.841 2.3049 10.86 2.3487 10.88 2.3925C10.896 2.4295 10.911 2.4665 10.926 2.5039C10.939 2.5351 10.952 2.5661 10.964 2.5976C10.983 2.6491 11.001 2.7008 11.018 2.7529C11.031 2.7927 11.043 2.8328 11.055 2.873C11.068 2.9168 11.08 2.9607 11.091 3.0048C11.103 3.0495 11.113 3.0945 11.123 3.1396C11.131 3.176 11.138 3.2124 11.145 3.249C11.155 3.3009 11.165 3.3529 11.172 3.4052C11.178 3.4419 11.183 3.4787 11.187 3.5156C11.193 3.5598 11.197 3.604 11.201 3.6484C11.205 3.6939 11.209 3.7394 11.212 3.7851C11.213 3.8053 11.215 3.8254 11.215 3.8457C11.216 3.87 11.215 3.8946 11.215 3.9189C11.216 3.957 11.218 3.995 11.217 4.0332C11.217 4.0782 11.215 4.1231 11.214 4.1679C11.212 4.1966 11.212 4.2252 11.211 4.2539L11.207 4.2871C11.195 4.4545 11.173 4.621 11.14 4.7851C11.051 5.231 10.496 5.2142 10.335 4.789C10.299 4.6942 10.291 4.5913 10.307 4.4912C10.335 4.3177 10.349 4.1416 10.347 3.9648C10.347 3.9204 10.345 3.8762 10.342 3.832C10.315 3.3121 10.158 2.8081 9.885 2.3642C9.869 2.338 9.853 2.3118 9.837 2.2861C9.554 1.854 9.169 1.4989 8.715 1.2519C8.672 1.2281 8.627 1.2053 8.583 1.1836C8.131 0.9646 7.633 0.857 7.13 0.8711C7.085 0.8723 7.04 0.8747 6.995 0.8779C6.48 0.9148 5.982 1.0784 5.546 1.3545C5.523 1.3687 5.5 1.3826 5.478 1.3974C5.045 1.6874 4.691 2.0817 4.449 2.5429C4.431 2.5762 4.414 2.6097 4.398 2.6435C4.18 3.0979 4.075 3.5981 4.091 4.1015C4.096 4.2323 4.109 4.3625 4.13 4.4912C4.146 4.5913 4.138 4.6942 4.102 4.789C4.095 4.8083 4.086 4.8261 4.077 4.8437C4.086 4.8616 4.086 4.8609 4.076 4.8447C3.889 5.214 3.382 5.2104 3.297 4.7851C3.264 4.6209 3.241 4.4546 3.229 4.2871L3.226 4.2539C3.224 4.2252 3.224 4.1965 3.223 4.1679C3.221 4.1231 3.22 4.0782 3.219 4.0332C3.219 3.998 3.22 3.9628 3.22 3.9277C3.221 3.9005 3.22 3.8729 3.221 3.8457C3.222 3.8215 3.225 3.7975 3.226 3.7734C3.228 3.7392 3.23 3.705 3.233 3.6709C3.237 3.617 3.243 3.5633 3.25 3.5097C3.254 3.4749 3.258 3.44 3.263 3.4052C3.271 3.3529 3.281 3.3009 3.291 3.249C3.298 3.2124 3.305 3.176 3.313 3.1396C3.323 3.0965 3.332 3.0535 3.343 3.0107C3.356 2.9594 3.37 2.9082 3.385 2.8574C3.394 2.829 3.403 2.8006 3.412 2.7724C3.431 2.7137 3.452 2.6555 3.473 2.5976C3.485 2.5663 3.497 2.5349 3.509 2.5039C3.524 2.4665 3.54 2.4294 3.556 2.3925C3.576 2.3487 3.595 2.3049 3.616 2.2617C3.624 2.2439 3.632 2.2256 3.641 2.208C3.652 2.1856 3.665 2.1637 3.676 2.1416C3.697 2.1028 3.718 2.0645 3.74 2.0263C3.761 1.9893 3.783 1.9532 3.805 1.917C3.818 1.8961 3.829 1.8741 3.842 1.8535C3.853 1.8374 3.863 1.8215 3.874 1.8056C3.9 1.7657 3.927 1.7264 3.955 1.6875C3.978 1.6551 4.001 1.6233 4.025 1.5918C4.057 1.5492 4.09 1.507 4.124 1.4658C4.141 1.4441 4.159 1.4226 4.177 1.4013C4.217 1.3547 4.258 1.3094 4.3 1.2646C4.319 1.2443 4.339 1.224 4.358 1.2041C4.398 1.1627 4.44 1.1225 4.482 1.083C4.507 1.0596 4.532 1.0363 4.557 1.0136C4.592 0.9823 4.628 0.9519 4.665 0.9218C4.696 0.8957 4.728 0.8699 4.76 0.8447C4.803 0.8113 4.847 0.7787 4.891 0.747C4.914 0.7304 4.938 0.7143 4.962 0.6982C4.984 0.6825 5.007 0.6656 5.03 0.6504C5.059 0.6311 5.09 0.6132 5.12 0.5947C5.138 0.5833 5.157 0.5716 5.175 0.5605C5.23 0.5279 5.286 0.4967 5.342 0.4668C5.356 0.4597 5.369 0.4522 5.382 0.4453C5.387 0.4429 5.392 0.4408 5.396 0.4384C5.621 0.3233 5.856 0.2297 6.098 0.1591C6.131 0.1495 6.165 0.1415 6.198 0.1328C6.242 0.1212 6.286 0.1096 6.33 0.0996C6.369 0.0907 6.408 0.0838 6.447 0.0761C6.489 0.0679 6.531 0.0596 6.573 0.0527C6.611 0.0465 6.648 0.0402 6.686 0.0351C6.708 0.0322 6.729 0.0289 6.751 0.0263L6.839 0.0185C6.872 0.0154 6.905 0.012 6.937 0.0097L7.014 0.0039C7.04 0.0026 7.065 0.0027 7.09 0.0019C7.133 0.0005 7.176 0 7.218 0C7.261 0 7.303 0.0006 7.345 0.0019Z"
      fill="currentColor"
    />
  </svg>
);

export const SelectIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="select"
    component={SelectSvg}
    onClick={onClick}
    className={anticonClass('select', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const EditSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19.87 19.99" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.435 19.1193C19.675 19.1193 19.87 19.3137 19.87 19.5539C19.87 19.7942 19.675 19.9885 19.435 19.9885H0.435C0.195 19.9885 0 19.7942 0 19.5539C0 19.3137 0.195 19.1193 0.435 19.1193H19.435Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.784 0.1492C15.928 0 16.16 0 16.304 0.1492L19.197 3.1648C19.343 3.3163 19.343 3.564 19.197 3.7166L5.738 16.1775L5.734 16.1814C5.656 16.2627 5.553 16.2989 5.452 16.2918H5.443L2.53 16.3523C2.332 16.356 2.161 16.1921 2.155 15.9763L2.214 12.8816C2.216 12.7767 2.258 12.6824 2.322 12.616L2.36 12.576L15.78 0.1531L15.784 0.1492ZM2.976 13.0242L2.945 13.0535V13.0955L2.898 15.5711L3.002 15.5691L5.278 15.5232L5.316 15.5222L5.344 15.4959L18.344 3.5144L18.418 3.4451L16.116 1.0476L16.049 0.9763L2.976 13.0242Z"
      fill="currentColor"
    />
  </svg>
);

export const EditIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="edit"
    component={EditSvg}
    onClick={onClick}
    className={anticonClass('edit', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

// PinSvg traces both subpaths (outer + inner), producing a hollow/outline pin.
const PinSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.3 20.31" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.34 0.0049C13.46 0 13.578 0.0447 13.663 0.1289L20.16 6.5127C20.248 6.5988 20.295 6.718 20.29 6.8408C20.285 6.9635 20.228 7.0786 20.134 7.1572L14.192 12.1064L14.605 16.0088C14.619 16.1406 14.571 16.2714 14.477 16.3643L12.592 18.2158C12.423 18.382 12.152 18.3821 11.983 18.2158L7.361 13.6748L0.783 20.1403C0.612 20.3084 0.336 20.3057 0.168 20.1344C0 19.963 0.002 19.6875 0.174 19.5192L6.741 13.0654L1.773 8.1846C1.69 8.1029 1.642 7.9916 1.642 7.875C1.642 7.7583 1.689 7.6462 1.773 7.5645L3.658 5.7129L3.733 5.6533C3.814 5.6031 3.91 5.58 4.006 5.5898L7.987 5.9951L13.029 0.1553C13.107 0.0644 13.22 0.0099 13.34 0.0049ZM8.496 6.7363C8.404 6.8437 8.264 6.8992 8.122 6.8848L4.12 6.4766L2.698 7.874L12.287 17.2959L13.717 15.8906L13.303 11.9678C13.288 11.8234 13.346 11.6809 13.457 11.5879L19.207 6.7959L13.384 1.0752L8.496 6.7363Z"
      fill="currentColor"
    />
  </svg>
);

export const PinIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="pin"
    component={PinSvg}
    onClick={onClick}
    className={anticonClass('pin', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

// Outer contour of PinSvg traced alone (no inner cutout), producing a solid pin for the "pinned" state.
const PinFilledSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.3 20.31" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.34 0.0049C13.46 0 13.578 0.0447 13.663 0.1289L20.16 6.5127C20.248 6.5988 20.295 6.718 20.29 6.8408C20.285 6.9635 20.228 7.0786 20.134 7.1572L14.192 12.1064L14.605 16.0088C14.619 16.1406 14.571 16.2714 14.477 16.3643L12.592 18.2158C12.423 18.382 12.152 18.3821 11.983 18.2158L7.361 13.6748L0.783 20.1403C0.612 20.3084 0.336 20.3057 0.168 20.1344C0 19.963 0.002 19.6875 0.174 19.5192L6.741 13.0654L1.773 8.1846C1.69 8.1029 1.642 7.9916 1.642 7.875C1.642 7.7583 1.689 7.6462 1.773 7.5645L3.658 5.7129L3.733 5.6533C3.814 5.6031 3.91 5.58 4.006 5.5898L7.987 5.9951L13.029 0.1553C13.107 0.0644 13.22 0.0099 13.34 0.0049Z"
      fill="currentColor"
    />
  </svg>
);

export const PinFilledIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="pin-filled"
    component={PinFilledSvg}
    onClick={onClick}
    className={anticonClass('pin-filled', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

// ===========================================================================
// WEB / UPLOAD / CONTEXT / MATTERS / SUBMIT / SORT / FILTER ICONS
// ===========================================================================

// Globe icon previously used as TranslationIcon's artwork; kept as a generic "web" icon
// now that TranslationIcon has its own dedicated translate-glyph design.
export const WebIcon: FC<IconProps> = ({ size = 24, color = '#90A2AB' }) => (
  <SimpleIconSvg size={size} viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0ZM9.95117 13.5605C8.58384 13.5675 7.23438 13.8678 5.99316 14.4404C6.23128 15.241 6.52921 15.9646 6.87305 16.5898C7.80233 18.2794 8.94512 19.0684 10 19.0684C11.0549 19.0684 12.1977 18.2794 13.127 16.5898C13.4709 15.9644 13.7677 15.2402 14.0059 14.4395C13.9922 14.4331 13.9787 14.4262 13.9648 14.4199C12.7051 13.8462 11.3354 13.5535 9.95117 13.5605ZM5.20508 14.8369C4.49538 15.2248 3.88312 15.6572 3.31836 16.2188C4.41456 17.396 5.81723 18.2824 7.40039 18.752C6.47998 17.8525 5.71714 16.4906 5.20508 14.8369ZM14.7822 14.873C14.2702 16.5105 13.5125 17.8591 12.5986 18.752C14.0638 18.3176 15.3753 17.5273 16.4316 16.4785C15.846 15.7975 15.4018 15.2915 14.7822 14.873ZM15.4932 10.4375C15.459 11.6999 15.2937 12.9002 15.0254 13.9971C15.8559 14.501 16.3971 15.1033 17.0234 15.8311C18.2511 14.3539 19.0226 12.4844 19.1191 10.4375H15.4932ZM0.880859 10.4375C0.971492 12.3585 1.65722 14.1228 2.75586 15.5537C3.4226 14.9011 4.14335 14.4088 4.96777 13.9756C4.70267 12.8846 4.54073 11.6916 4.50684 10.4375H0.880859ZM5.37695 10.4375C5.40867 11.5563 5.54644 12.6163 5.76855 13.5859C7.08367 13.0026 8.50612 12.6978 9.94629 12.6904C11.4219 12.6829 12.8823 12.989 14.2305 13.5869C14.4527 12.617 14.5913 11.5567 14.623 10.4375H5.37695ZM2.77051 4.42578C1.66195 5.86142 0.970981 7.63551 0.880859 9.56738H4.50488C4.53103 8.2655 4.694 7.03073 4.96875 5.90723C4.1792 5.50468 3.43606 5.03299 2.77051 4.42578ZM14.2285 6.29004C12.9414 6.8605 11.5512 7.16544 10.1416 7.18457C8.63892 7.20494 7.14953 6.90042 5.77539 6.29199L5.77051 6.29004C5.53853 7.29394 5.39964 8.3979 5.375 9.56738H14.625C14.6004 8.39782 14.4605 7.294 14.2285 6.29004ZM17.2402 4.44043C16.5668 5.07518 15.8489 5.49797 15.0303 5.90625C15.3052 7.03006 15.469 8.26505 15.4951 9.56738H19.1191C19.0293 7.64196 18.3423 5.87353 17.2402 4.44043ZM10 0.870117C8.93982 0.870117 7.79849 1.65026 6.87207 3.32031C6.53091 3.93533 6.2343 4.64738 5.99707 5.43652C6.04047 5.45615 6.08316 5.47768 6.12695 5.49707C7.3866 6.05485 8.7524 6.33313 10.1299 6.31445C11.4685 6.29627 12.7869 5.99542 14.002 5.43555C13.7648 4.64686 13.4689 3.93503 13.1279 3.32031C12.2015 1.65026 11.0602 0.870117 10 0.870117ZM7.2959 1.27637C5.76322 1.75091 4.40462 2.62015 3.33496 3.7627C3.89217 4.27561 4.51908 4.69002 5.20605 5.05273C5.69857 3.47272 6.4227 2.16527 7.2959 1.27637ZM12.7031 1.27637C13.5762 2.16489 14.3004 3.4714 14.793 5.05078C15.5189 4.68018 16.1195 4.31007 16.6768 3.77637C15.605 2.6271 14.242 1.75267 12.7031 1.27637Z"
      fill={color}
    />
  </SimpleIconSvg>
);

const UploadSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19.87 20.06" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.8692 18.6281C0.8692 18.9401 1.1226 19.1935 1.4346 19.1935H18.4346C18.7466 19.1935 19 18.9401 19 18.6281V13.6281H19.8696V18.6281C19.8696 19.4205 19.2271 20.0626 18.4346 20.0626H1.4346C0.6421 20.0626 0.0001 19.4205 0 18.6281V13.6281H0.8692V18.6281Z"
      fill="currentColor"
    />
    <path
      d="M9.5293 0.1593C9.6581 0.0309 9.8479 0 10.0059 0.0665C10.0828 0.0841 10.1569 0.122 10.2168 0.1818L13.8184 4.0177C13.9872 4.1874 13.9872 4.4623 13.8184 4.632C13.6487 4.8011 13.3729 4.8019 13.2032 4.6329L10.3213 1.5167V14.9816C10.321 15.2211 10.1262 15.4155 9.8867 15.4162C9.647 15.4158 9.4516 15.2213 9.4512 14.9816V1.4679L6.1768 4.6329C6.0072 4.8017 5.7322 4.8015 5.5625 4.6329C5.3931 4.4633 5.3925 4.1876 5.5616 4.0177L9.5293 0.1593Z"
      fill="currentColor"
    />
  </svg>
);

export const UploadIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="upload"
    component={UploadSvg}
    onClick={onClick}
    className={anticonClass('upload', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const ContextSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19.32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.838 6.5082C11.664 6.6018 13.116 8.1294 13.116 10L13.112 10.1795C13.02 12.0268 11.51 13.4955 9.661 13.4958L9.483 13.4918C7.716 13.4011 6.299 11.9672 6.21 10.1795L6.205 10C6.205 8.069 7.752 6.5033 9.661 6.5033L9.838 6.5082ZM9.661 7.3821C8.232 7.3821 7.073 8.5543 7.073 10C7.074 11.4455 8.232 12.6169 9.661 12.6169C11.09 12.6167 12.247 11.4454 12.248 10C12.248 8.5545 11.09 7.3824 9.661 7.3821Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.865 0C11.496 0.0003 12.006 0.5089 12.006 1.1284V2.5528L12.076 2.5745C13.138 2.913 14.118 3.4746 14.948 4.2188L15.002 4.2661L15.064 4.2306L16.245 3.5559C16.509 3.4052 16.817 3.3652 17.113 3.4435C17.408 3.5216 17.653 3.7083 17.805 3.9682L19.009 6.0337C19.322 6.5705 19.136 7.2604 18.592 7.5725L17.411 8.2472L17.347 8.2847L17.363 8.3577C17.479 8.8958 17.538 9.4468 17.538 10C17.538 10.5529 17.479 11.1039 17.363 11.6423L17.347 11.7143L17.411 11.7518L18.591 12.4265C18.854 12.5769 19.043 12.8207 19.121 13.1121C19.2 13.403 19.161 13.706 19.009 13.9663L17.805 16.0318C17.511 16.5364 16.878 16.7314 16.349 16.4973L16.245 16.4451L15.063 15.7694L15.001 15.7339L14.948 15.7812C14.118 16.5255 13.136 17.087 12.075 17.4255L12.006 17.4482V18.8716C12.006 19.4913 11.495 19.9997 10.864 20H8.457C7.826 19.9999 7.316 19.4911 7.316 18.8716V17.4482L7.246 17.4255C6.185 17.087 5.204 16.5255 4.374 15.7812L4.321 15.7339L4.259 15.7694L3.077 16.4451C2.531 16.7573 1.83 16.5695 1.517 16.0318L0.313 13.9663C0 13.4293 0.186 12.7394 0.73 12.4275L1.911 11.7518L1.975 11.7153L1.959 11.6423C1.843 11.1038 1.784 10.5531 1.784 10C1.784 9.4467 1.843 8.896 1.959 8.3577L1.975 8.2847L1.911 8.2472L0.731 7.5725C0.187 7.2609 0.001 6.5703 0.314 6.0327L1.517 3.9682C1.831 3.4305 2.53 3.244 3.077 3.5549L4.259 4.2306L4.32 4.2661L4.374 4.2188C5.203 3.475 6.185 2.913 7.247 2.5745L7.316 2.5528V1.1284C7.316 0.5087 7.827 0 8.458 0H10.865ZM8.457 0.8207C8.291 0.8207 8.149 0.9586 8.149 1.1284V2.8605C8.149 3.0449 8.022 3.2078 7.84 3.2561C6.722 3.5516 5.692 4.1211 4.845 4.9063L4.678 5.0661C4.544 5.1977 4.338 5.2256 4.175 5.1322L2.659 4.2651C2.533 4.1944 2.371 4.2224 2.275 4.3273L2.238 4.3776L1.035 6.4421C0.95 6.5889 1.002 6.7809 1.148 6.8643L2.662 7.7293C2.825 7.8226 2.904 8.012 2.856 8.19C2.697 8.7782 2.617 9.3872 2.617 10C2.617 10.6121 2.697 11.2218 2.856 11.81C2.904 11.9878 2.826 12.1762 2.663 12.2697L1.148 13.1357C1.003 13.2192 0.95 13.4104 1.035 13.5569L2.239 15.6224C2.324 15.7661 2.515 15.8164 2.66 15.7339L4.175 14.8678C4.24 14.8307 4.312 14.8127 4.383 14.8126C4.491 14.8127 4.599 14.8548 4.679 14.9339C5.556 15.8026 6.65 16.4282 7.841 16.7429C8.023 16.7912 8.149 16.9551 8.149 17.1395V18.8706C8.149 19.0401 8.292 19.1783 8.458 19.1783H10.865C11.031 19.178 11.173 19.0399 11.173 18.8706V17.1395C11.173 16.955 11.298 16.7912 11.481 16.7429C12.672 16.4281 13.766 15.8025 14.643 14.9339C14.776 14.8022 14.983 14.7751 15.147 14.8688L16.663 15.7349C16.807 15.8153 16.999 15.7678 17.084 15.6224L18.287 13.5579C18.342 13.4616 18.329 13.3678 18.317 13.3231C18.305 13.2779 18.269 13.1916 18.174 13.1367L16.659 12.2697C16.496 12.1765 16.418 11.9877 16.466 11.81C16.624 11.2216 16.705 10.6119 16.705 10C16.705 9.3872 16.624 8.7781 16.466 8.19C16.417 8.0119 16.495 7.8229 16.659 7.7293L18.173 6.8633C18.317 6.7796 18.372 6.5884 18.287 6.4421L17.083 4.3766C17.028 4.2824 16.941 4.2477 16.896 4.2355C16.861 4.2253 16.801 4.2166 16.733 4.2355L16.662 4.2651L15.147 5.1312C14.983 5.2257 14.776 5.1981 14.643 5.0661C13.767 4.1973 12.673 3.5714 11.481 3.2561C11.298 3.2079 11.172 3.045 11.172 2.8605V1.1284C11.172 0.9588 11.03 0.821 10.864 0.8207H8.457Z"
      fill="currentColor"
    />
  </svg>
);

export const ContextIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="context"
    component={ContextSvg}
    onClick={onClick}
    className={anticonClass('context', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const MattersSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 18.03 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.968 8.3643C10.121 8.3644 10.271 8.4269 10.383 8.542C10.495 8.6577 10.561 8.8177 10.561 8.9863C10.561 9.1551 10.496 9.3158 10.383 9.4316C10.271 9.5468 10.121 9.6093 9.968 9.6094C9.815 9.6093 9.665 9.5467 9.553 9.4316C9.44 9.3158 9.374 9.1552 9.374 8.9863C9.374 8.8177 9.44 8.6577 9.553 8.542C9.665 8.4269 9.815 8.3643 9.968 8.3643Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.004 0V0.9326H13.194V5.9453L13.221 6.0166L17.834 18.3008L17.874 18.4238C18.028 19.0001 17.763 19.6039 17.259 19.8672L17.147 19.918C17.006 19.9725 16.86 20 16.716 20H1.198C0.549 19.9998 0 19.4527 0 18.7549C0 18.6021 0.027 18.4483 0.081 18.3008L4.694 6.0166L4.721 5.9453V0.9326H2.91V0H15.004ZM12.578 13.457C11.34 13.4571 10.174 13.9021 9.245 14.6973L9.244 14.6982C8.154 15.635 6.785 16.1552 5.342 16.1553C4.37 16.1552 3.433 15.9209 2.591 15.4814L2.174 15.2637L0.743 19.0674H17.171L15.383 14.2979L15.263 14.2227L14.956 14.0479C14.23 13.6631 13.421 13.4571 12.578 13.457ZM5.605 0.9844V6.123L2.507 14.373L2.815 14.5498C3.576 14.9854 4.44 15.2186 5.339 15.2188L5.34 15.2197C6.56 15.2213 7.739 14.7807 8.673 13.9785C9.763 13.0417 11.132 12.5206 12.575 12.5205C13.09 12.5205 13.596 12.5864 14.083 12.7148L14.862 12.9209L12.311 6.123V0.9844H5.605Z"
      fill="currentColor"
    />
  </svg>
);

export const MattersIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="matters"
    component={MattersSvg}
    onClick={onClick}
    className={anticonClass('matters', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const SubmitSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 14.01 19.97" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.88 0.23C7.06 0.03 7.54 0 7.74 0.26L13.8 6.89C14.01 7.11 14.01 7.46 13.8 7.67C13.62 7.85 13.34 7.88 13.14 7.74L13.06 7.67L7.86 1.9V19.43C7.86 19.74 7.62 19.97 7.33 19.97C7.04 19.97 6.8 19.73 6.8 19.43V1.85L0.95 7.67C0.75 7.88 0.42 7.88 0.21 7.67C0 7.46 0 7.11 0.21 6.89L6.87 0.23H6.88Z"
      fill="currentColor"
    />
  </svg>
);

export const SubmitIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="submit"
    component={SubmitSvg}
    onClick={onClick}
    className={anticonClass('submit', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const SortSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.17 19.84" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.712 15.1096C9.543 14.94 9.543 14.6652 9.712 14.4954C9.882 14.3256 10.158 14.325 10.328 14.4944L14.156 18.3215L14.156 0.4671C14.156 0.2272 14.35 0.0327 14.59 0.0325C14.83 0.0326 15.026 0.2271 15.026 0.4671L15.026 18.3704L19.387 14.009C19.557 13.8399 19.832 13.84 20.001 14.009C20.171 14.1788 20.172 14.4544 20.002 14.6243L14.948 19.679C14.819 19.8077 14.629 19.8385 14.471 19.7717C14.394 19.7543 14.32 19.7166 14.26 19.6565L9.712 15.1096Z"
      fill="currentColor"
    />
    <path
      d="M0.17 5.8294C0.001 5.6597 0 5.384 0.169 5.2141L5.224 0.1594C5.353 0.0307 5.542 0 5.701 0.0667C5.778 0.0841 5.851 0.1219 5.912 0.1819L10.459 4.7288C10.629 4.8985 10.628 5.1732 10.459 5.343C10.29 5.5125 10.014 5.5134 9.844 5.344L6.016 1.5169L6.016 19.3714C6.016 19.6112 5.821 19.8057 5.581 19.8059C5.342 19.8055 5.146 19.6111 5.146 19.3714L5.146 1.468L0.785 5.8294C0.615 5.9983 0.34 5.9983 0.17 5.8294Z"
      fill="currentColor"
    />
  </svg>
);

export const SortIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="sort"
    component={SortSvg}
    onClick={onClick}
    className={anticonClass('sort', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const FilterSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.878 12.8107C13.051 12.8107 13.198 12.9565 13.198 13.1384V13.6723C13.198 13.8543 13.051 14 12.878 14H7.122C6.949 14 6.802 13.8543 6.802 13.6723V13.1384C6.802 12.9565 6.949 12.8107 7.122 12.8107H12.878Z"
      fill="currentColor"
    />
    <path
      d="M14.972 8.5401C15.144 8.5403 15.29 8.6861 15.29 8.8679V9.4017C15.29 9.5836 15.144 9.7293 14.972 9.7294H5.028C4.856 9.7293 4.71 9.5836 4.71 9.4017V8.8679C4.71 8.6861 4.856 8.5403 5.028 8.5401H14.972Z"
      fill="currentColor"
    />
    <path
      d="M17.064 4.2706C17.237 4.2706 17.384 4.4163 17.384 4.5983V5.1321C17.384 5.314 17.237 5.4599 17.064 5.4599H2.936C2.763 5.4599 2.616 5.314 2.616 5.1321V4.5983C2.616 4.4163 2.763 4.2706 2.936 4.2706H17.064Z"
      fill="currentColor"
    />
    <path
      d="M19.681 0C19.854 0.0001 20 0.1458 20 0.3277V0.8616C20 1.0435 19.854 1.1892 19.681 1.1893H0.319C0.146 1.1892 0 1.0435 0 0.8616V0.3277C0 0.1458 0.146 0.0001 0.319 0H19.681Z"
      fill="currentColor"
    />
  </svg>
);

export const FilterIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="filter"
    component={FilterSvg}
    onClick={onClick}
    className={anticonClass('filter', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const CalendarSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.4346 0C14.6747 0 14.8699 0.1945 14.8701 0.4346V2.5H19L19.1025 2.5049C19.6067 2.5562 20 2.9823 20 3.5V19C20 19.5177 19.6067 19.9438 19.1025 19.9951L19 20H1C0.4823 20 0.0562 19.6067 0.0049 19.1025L0 19V3.5C0 2.9477 0.4477 2.5 1 2.5H5V0.4346C5.0002 0.1947 5.1947 0.0002 5.4346 0C5.6747 0 5.8699 0.1945 5.8701 0.4346V2.5H14V0.4346C14.0002 0.1947 14.1947 0.0002 14.4346 0ZM0.8701 19C0.8701 19.0718 0.9282 19.1299 1 19.1299H19C19.0718 19.1299 19.1299 19.0718 19.1299 19V9H0.8701V19ZM1 3.3701C0.9282 3.3701 0.8701 3.4282 0.8701 3.5V8.1299H19.1299V3.5C19.1299 3.4282 19.0718 3.3701 19 3.3701H1Z"
      fill="currentColor"
    />
  </svg>
);

export const CalendarIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="calendar"
    component={CalendarSvg}
    onClick={onClick}
    className={anticonClass('calendar', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const VisibilitySvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 1.8764C12.385 1.8764 14.292 3.7091 14.292 6C14.292 8.2909 12.385 10.1236 10 10.1236C7.616 10.1236 5.709 8.2909 5.709 6C5.709 3.7091 7.616 1.8764 10 1.8764ZM10 2.6188C9.067 2.6188 8.172 2.9746 7.512 3.6088C6.852 4.243 6.481 5.1032 6.481 6C6.481 6.8968 6.852 7.757 7.512 8.3912C8.172 9.0254 9.067 9.3812 10 9.3812C10.934 9.3812 11.829 9.0254 12.489 8.3912C13.149 7.757 13.52 6.8968 13.52 6C13.52 5.1032 13.149 4.243 12.489 3.6088C11.829 2.9746 10.934 2.6188 10 2.6188Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0C14.394 0 18.192 2.4445 20 6C18.192 9.5555 14.394 12 10 12C5.607 12 1.808 9.5557 0 6C1.808 2.4443 5.607 0 10 0ZM10 0.7423C6.056 0.7423 2.645 2.8457 0.89 5.9529L0.863 6L0.89 6.0471C2.645 9.1543 6.056 11.2577 10 11.2577C13.945 11.2577 17.356 9.1543 19.111 6.0471L19.138 6L19.111 5.9529C17.356 2.8457 13.945 0.7423 10 0.7423Z"
      fill="currentColor"
    />
  </svg>
);

export const VisibilityIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="visibility"
    component={VisibilitySvg}
    onClick={onClick}
    className={anticonClass('visibility', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const ResetSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19 20.0569" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 9.1473C0 4.1002 4.259 0 9.501 0C14.742 0 19 4.1015 19 9.1473C19 13.1698 16.299 16.5923 12.543 17.8149L12.542 17.8149C12.293 17.8953 12.023 17.7657 11.938 17.527C11.897 17.4106 11.905 17.2846 11.961 17.1743C12.018 17.0651 12.116 16.9811 12.237 16.9416C15.611 15.8439 18.043 12.7728 18.043 9.1474C18.043 4.5994 14.224 0.9227 9.501 0.9227C4.777 0.9227 0.956 4.5997 0.956 9.1474C0.956 13.3462 4.214 16.8 8.431 17.3061L7.355 16.0393C7.275 15.9445 7.237 15.8221 7.249 15.701C7.263 15.5798 7.325 15.467 7.424 15.3902C7.522 15.3135 7.649 15.2763 7.775 15.2895C7.901 15.3015 8.018 15.3615 8.098 15.4562L9.868 17.5424C9.878 17.5556 9.887 17.5688 9.895 17.5832C9.905 17.5964 9.913 17.6108 9.922 17.6252C9.951 17.6828 9.968 17.7452 9.971 17.8088C9.974 17.8172 9.977 17.8256 9.978 17.8351C9.977 17.8411 9.976 17.8483 9.973 17.8555C9.969 17.9191 9.952 17.9815 9.921 18.0391C9.914 18.0535 9.907 18.0667 9.898 18.0799C9.872 18.1207 9.838 18.1591 9.8 18.1902L7.633 19.895C7.43 20.0569 7.128 20.0281 6.96 19.8326L6.96 19.8314C6.793 19.6346 6.824 19.3443 7.029 19.1836L8.26 18.2143C3.605 17.6264 0.001 13.7874 0 9.1473Z"
      fill="currentColor"
    />
  </svg>
);

export const ResetIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="reset"
    component={ResetSvg}
    onClick={onClick}
    className={anticonClass('reset', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const ReuseSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19 20.0569" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19 9.1473C19 4.1002 14.741 0 9.499 0C4.258 0 0 4.1015 0 9.1473C0 13.1698 2.701 16.5923 6.457 17.8149L6.458 17.8149C6.707 17.8953 6.977 17.7657 7.062 17.527C7.103 17.4106 7.095 17.2846 7.039 17.1743C6.982 17.0651 6.884 16.9811 6.763 16.9416C3.389 15.8439 0.957 12.7728 0.957 9.1474C0.957 4.5994 4.776 0.9227 9.499 0.9227C14.223 0.9227 18.044 4.5997 18.044 9.1474C18.044 13.3462 14.786 16.8 10.569 17.3061L11.645 16.0393C11.725 15.9445 11.763 15.8221 11.751 15.701C11.737 15.5798 11.675 15.467 11.576 15.3902C11.478 15.3135 11.351 15.2763 11.225 15.2895C11.099 15.3015 10.982 15.3615 10.902 15.4562L9.132 17.5424C9.122 17.5556 9.113 17.5688 9.105 17.5832C9.095 17.5964 9.087 17.6108 9.078 17.6252C9.049 17.6828 9.032 17.7452 9.029 17.8088C9.026 17.8172 9.023 17.8256 9.022 17.8351C9.023 17.8411 9.024 17.8483 9.027 17.8555C9.031 17.9191 9.048 17.9815 9.079 18.0391C9.086 18.0535 9.093 18.0667 9.102 18.0799C9.128 18.1207 9.162 18.1591 9.2 18.1902L11.367 19.895C11.57 20.0569 11.872 20.0281 12.04 19.8326L12.04 19.8314C12.207 19.6346 12.176 19.3443 11.971 19.1836L10.74 18.2143C15.395 17.6264 18.999 13.7874 19 9.1473Z"
      fill="currentColor"
    />
  </svg>
);

export const ReuseIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="reuse"
    component={ReuseSvg}
    onClick={onClick}
    className={anticonClass('reuse', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const InverseSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19.838 20.1715" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.109 10.4594C14.94 10.6286 14.665 10.6284 14.495 10.4594C14.325 10.2897 14.325 10.0141 14.494 9.8442L18.321 6.0161L0.467 6.0161C0.227 6.0158 0.032 5.8214 0.032 5.5815C0.032 5.3415 0.227 5.1462 0.467 5.146L18.37 5.146L14.009 0.7846C13.84 0.6149 13.84 0.3401 14.009 0.1704C14.179 0.0007 14.454 0 14.624 0.1694L19.679 5.2241C19.807 5.353 19.838 5.5425 19.772 5.7006C19.754 5.7779 19.716 5.8514 19.656 5.9116L15.109 10.4594Z"
      fill="currentColor"
    />
    <path
      d="M5.829 20.0014C5.659 20.1709 5.384 20.1715 5.214 20.0024L0.159 14.9477C0.03 14.8189 0 14.6293 0.066 14.4712C0.084 14.394 0.122 14.3204 0.182 14.2602L4.729 9.7124C4.898 9.5432 5.173 9.5433 5.343 9.7124C5.512 9.8821 5.513 10.1578 5.344 10.3276L1.517 14.1557L19.371 14.1557C19.611 14.156 19.805 14.3504 19.806 14.5903C19.805 14.83 19.611 15.0256 19.371 15.0258L1.468 15.0258L5.829 19.3872C5.998 19.5569 5.998 19.8317 5.829 20.0014Z"
      fill="currentColor"
    />
  </svg>
);

export const InverseIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="inverse"
    component={InverseSvg}
    onClick={onClick}
    className={anticonClass('inverse', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const ExitSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 14.5736 14.4819" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.1695 0.1703C0.3392 0.0005 0.6148 0 0.7847 0.1696L7.2868 6.6716L13.7889 0.1696C13.9587 0.0001 14.2344 0.0006 14.4041 0.1703C14.5736 0.34 14.5735 0.615 14.4041 0.7848L7.9021 7.2869L14.3123 13.6971C14.4818 13.867 14.4813 14.1427 14.3116 14.3124C14.1418 14.4817 13.8668 14.4817 13.697 14.3124L7.2868 7.9022L0.8766 14.3124C0.7068 14.4819 0.4318 14.4819 0.262 14.3124C0.0923 14.1426 0.0918 13.867 0.2613 13.6971L6.6715 7.2869L0.1695 0.7848C0 0.6151 0.0001 0.3401 0.1695 0.1703Z"
      fill="currentColor"
    />
  </svg>
);

export const ExitIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="exit"
    component={ExitSvg}
    onClick={onClick}
    className={anticonClass('exit', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const AddSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.9346 0C10.1747 0 10.3699 0.1945 10.3701 0.4346V9.6299H19.5654C19.8055 9.6301 20 9.8253 20 10.0654C19.9998 10.3053 19.8053 10.4998 19.5654 10.5H10.3701V19.5654C10.3699 19.8055 10.1747 20 9.9346 20C9.6947 19.9998 9.5002 19.8053 9.5 19.5654V10.5H0.4346C0.1947 10.4998 0.0002 10.3053 0 10.0654C0 9.8253 0.1945 9.6301 0.4346 9.6299H9.5V0.4346C9.5002 0.1947 9.6947 0.0002 9.9346 0Z"
      fill="currentColor"
    />
  </svg>
);

export const AddIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="add"
    component={AddSvg}
    onClick={onClick}
    className={anticonClass('add', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const VerifySvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.619 6.6924C14.789 6.523 15.065 6.5228 15.234 6.6924C15.404 6.8621 15.404 7.1378 15.234 7.3076L8.995 13.5459L5.692 10.2451C5.523 10.0753 5.523 9.7998 5.692 9.6299C5.862 9.4602 6.138 9.4603 6.308 9.6299L8.994 12.3154L14.619 6.6924Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0C15.523 0 20 4.4772 20 10C20 15.5228 15.523 20 10 20C4.477 20 0 15.5228 0 10C0 4.4772 4.477 0 10 0ZM10 0.8701C4.958 0.8701 0.87 4.9576 0.87 10C0.87 15.0424 4.958 19.1299 10 19.1299C15.042 19.1299 19.13 15.0424 19.13 10C19.13 4.9576 15.042 0.8701 10 0.8701Z"
      fill="currentColor"
    />
  </svg>
);

export const VerifyIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="verify"
    component={VerifySvg}
    onClick={onClick}
    className={anticonClass('verify', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const TooltipInfoSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0C15.523 0 20 4.4772 20 10C20 15.5228 15.523 20 10 20C4.477 20 0 15.5228 0 10C0 4.4772 4.477 0 10 0ZM10 0.8701C4.958 0.8701 0.87 4.9576 0.87 10C0.87 15.0424 4.958 19.1299 10 19.1299C15.042 19.1299 19.13 15.0424 19.13 10C19.13 4.9576 15.042 0.8701 10 0.8701Z"
      fill="currentColor"
    />
    <line
      x1="10.035"
      y1="8.435"
      x2="10.035"
      y2="13.565"
      stroke="currentColor"
      strokeWidth="0.87"
      strokeLinecap="round"
    />
    <circle cx="10" cy="6.25" r="0.75" fill="currentColor" />
  </svg>
);

export const TooltipInfoIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="tooltip-info"
    component={TooltipInfoSvg}
    onClick={onClick}
    className={anticonClass('tooltip-info', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const TooltipHelpSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0C15.523 0 20 4.4772 20 10C20 15.5228 15.523 20 10 20C4.477 20 0 15.5228 0 10C0 4.4772 4.477 0 10 0ZM10 0.8701C4.958 0.8701 0.87 4.9576 0.87 10C0.87 15.0424 4.958 19.1299 10 19.1299C15.042 19.1299 19.13 15.0424 19.13 10C19.13 4.9576 15.042 0.8701 10 0.8701Z"
      fill="currentColor"
    />
    <path
      d="M9.604 13.7998C9.877 13.7993 10.096 13.8905 10.279 14.0742C10.463 14.2584 10.554 14.4774 10.554 14.75C10.554 15.0219 10.463 15.2399 10.28 15.4238L10.209 15.4883C10.038 15.6297 9.84 15.6997 9.604 15.7002C9.334 15.7007 9.115 15.6099 8.929 15.4248C8.744 15.2406 8.652 15.0225 8.653 14.751C8.654 14.4779 8.746 14.2588 8.929 14.0752C9.111 13.892 9.331 13.8003 9.604 13.7998Z"
      fill="currentColor"
    />
    <path
      d="M9.754 4.2998C10.607 4.3005 11.346 4.551 11.981 5.0488C12.603 5.5355 12.903 6.1461 12.903 6.9004C12.904 7.3789 12.782 7.8063 12.538 8.1895C12.277 8.6007 11.964 8.9813 11.599 9.3311C11.324 9.5892 11.066 9.8603 10.825 10.1436C10.559 10.4573 10.385 10.8172 10.309 11.2188C10.27 11.4104 10.187 11.5615 10.06 11.6826C9.942 11.7941 9.805 11.8496 9.629 11.8496C9.472 11.8501 9.339 11.7999 9.218 11.6904C9.104 11.5879 9.054 11.4723 9.054 11.3252L9.06 11.1641C9.088 10.7918 9.213 10.4562 9.435 10.1514C9.712 9.7703 10.034 9.4203 10.4 9.1016L10.402 9.1006C10.699 8.8393 10.966 8.5597 11.202 8.2617C11.469 7.9246 11.604 7.5329 11.604 7.0996C11.604 6.6108 11.411 6.1953 11.038 5.873C10.674 5.5583 10.241 5.4004 9.754 5.4004C9.408 5.4004 9.076 5.4773 8.761 5.6299C8.441 5.7848 8.174 6.009 7.962 6.2979L7.96 6.3008C7.832 6.4804 7.685 6.5723 7.52 6.6055C7.347 6.6399 7.181 6.6139 7.01 6.5176C6.905 6.4493 6.839 6.3545 6.812 6.208C6.783 6.0543 6.807 5.9148 6.888 5.7764L6.892 5.7686C7.161 5.2756 7.551 4.9118 8.068 4.6719C8.603 4.4237 9.164 4.2998 9.754 4.2998Z"
      fill="currentColor"
    />
  </svg>
);

export const TooltipHelpIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="tooltip-help"
    component={TooltipHelpSvg}
    onClick={onClick}
    className={anticonClass('tooltip-help', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const ArrowRightSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19.97 14.01" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.74 6.88C19.94 7.06 19.97 7.54 19.71 7.74L13.08 13.8C12.86 14.01 12.51 14.01 12.3 13.8C12.12 13.62 12.09 13.34 12.23 13.14L12.3 13.06L18.07 7.86L0.54 7.86C0.23 7.86 0 7.62 0 7.33C0 7.04 0.24 6.8 0.54 6.8L18.12 6.8L12.3 0.95C12.09 0.75 12.09 0.42 12.3 0.21C12.51 0 12.86 0 13.08 0.21L19.74 6.87L19.74 6.88Z"
      fill="currentColor"
    />
  </svg>
);

export const ArrowRightIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="arrow-right"
    component={ArrowRightSvg}
    onClick={onClick}
    className={anticonClass('arrow-right', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const ArrowLeftSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19.97 14.01" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.23 7.13C0.03 6.95 0 6.47 0.26 6.27L6.89 0.21C7.11 0 7.46 0 7.67 0.21C7.85 0.39 7.88 0.67 7.74 0.87L7.67 0.95L1.9 6.15L19.43 6.15C19.74 6.15 19.97 6.39 19.97 6.68C19.97 6.97 19.73 7.21 19.43 7.21L1.85 7.21L7.67 13.06C7.88 13.26 7.88 13.59 7.67 13.8C7.46 14.01 7.11 14.01 6.89 13.8L0.23 7.14L0.23 7.13Z"
      fill="currentColor"
    />
  </svg>
);

export const ArrowLeftIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="arrow-left"
    component={ArrowLeftSvg}
    onClick={onClick}
    className={anticonClass('arrow-left', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const ClipboardSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.0332 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.9512 0C17.1913 0 17.3865 0.1945 17.3867 0.4346V2.4756H18.0986C19.149 2.4757 19.9941 3.3099 19.9941 4.3301L20.0332 15C20.0332 16.0203 19.1872 16.8545 18.1367 16.8545H16.0664V18.1455C16.0664 19.1658 15.2194 20 14.1689 20H1.9287C0.8793 20 0.0332 19.1657 0.0332 18.1455L0 7.5127C0 6.4925 0.8471 5.6582 1.8965 5.6582H3.9678L3.9619 4.3301C3.9619 3.3099 4.808 2.4757 5.8584 2.4756H7.5166V0.4346C7.5168 0.1947 7.7113 0.0002 7.9512 0C8.1913 0 8.3865 0.1945 8.3867 0.4346V2.4756H16.5166V0.4346C16.5168 0.1947 16.7113 0.0002 16.9512 0ZM1.8965 6.5C1.3307 6.5 0.8662 6.9509 0.8662 7.5127L0.8984 18.1455C0.8984 18.7073 1.3629 19.1582 1.9287 19.1582H14.1689C14.7347 19.1582 15.1992 18.7073 15.1992 18.1455L15.167 7.5127C15.167 6.9509 14.7025 6.5 14.1367 6.5H1.8965ZM5.8584 3.3174C5.2927 3.3175 4.8281 3.7684 4.8281 4.3301L4.835 5.6582H14.1367C14.6402 5.6582 15.1242 5.8551 15.4795 6.2031C15.8343 6.551 16.0332 7.0226 16.0332 7.5127L16.0664 16.0127H18.1367C18.7025 16.0127 19.167 15.5618 19.167 15L19.1289 4.3301C19.1289 3.7685 18.6642 3.3176 18.0986 3.3174H5.8584Z"
      fill="currentColor"
    />
  </svg>
);

export const ClipboardIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="clipboard"
    component={ClipboardSvg}
    onClick={onClick}
    className={anticonClass('clipboard', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const TourSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.078 6.6072C13.155 6.6227 13.227 6.6611 13.282 6.7166C13.338 6.7725 13.376 6.8442 13.392 6.9217C13.407 6.9991 13.399 7.0792 13.369 7.1522L11.62 11.4022C11.6 11.4509 11.571 11.4957 11.533 11.533C11.496 11.5702 11.451 11.5999 11.402 11.6199L7.152 13.3699C7.08 13.4 6.999 13.4077 6.922 13.3924C6.845 13.377 6.773 13.3388 6.717 13.283C6.661 13.2273 6.623 13.1561 6.607 13.0789C6.592 13.0014 6.6 12.9205 6.63 12.8475L8.38 8.5975C8.4 8.5486 8.43 8.504 8.468 8.4666C8.505 8.4295 8.549 8.3997 8.598 8.3797L12.848 6.6297C12.921 6.5998 13.001 6.5919 13.078 6.6072ZM9.096 9.0399L9.057 9.0555L9.041 9.0946L7.827 12.0428L7.735 12.2645L7.957 12.1727L10.905 10.9588L10.943 10.9432L10.959 10.9041L12.173 7.9569L12.264 7.7352L9.096 9.0399Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0C15.523 0 20 4.4772 20 10C20 15.5228 15.523 20 10 20C4.4772 20 0 15.5228 0 10C0 4.4772 4.4772 0 10 0ZM10 0.8701C4.9576 0.8701 0.8701 4.9576 0.8701 10C0.8701 15.0424 4.9576 19.1299 10 19.1299C15.042 19.1299 19.13 15.0424 19.13 10C19.13 4.9576 15.042 0.8701 10 0.8701Z"
      fill="currentColor"
    />
  </svg>
);

export const TourIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="tour"
    component={TourSvg}
    onClick={onClick}
    className={anticonClass('tour', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const PlaySvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 18.4623 20.5899" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.7765 9.4208C18.4623 9.8018 18.4623 10.7881 17.7765 11.1691L1.4857 20.2196C0.8191 20.5899 0 20.1079 0 19.3455L0 1.2445C0 0.482 0.8191 0 1.4857 0.3703L17.7765 9.4208Z"
      fill="currentColor"
    />
  </svg>
);

export const PlayIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="play"
    component={PlaySvg}
    onClick={onClick}
    className={anticonClass('play', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const PauseSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="7" height="20" rx="1" fill="currentColor" />
    <rect x="13" y="0" width="7" height="20" rx="1" fill="currentColor" />
  </svg>
);

export const PauseIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="pause"
    component={PauseSvg}
    onClick={onClick}
    className={anticonClass('pause', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const StopSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="20" height="20" rx="1" fill="currentColor" />
  </svg>
);

export const StopIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="stop"
    component={StopSvg}
    onClick={onClick}
    className={anticonClass('stop', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const Plus10sSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19 20.0569" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 10.9096C0 15.9567 4.259 20.0569 9.501 20.0569C14.742 20.0569 19 15.9554 19 10.9096C19 6.8871 16.299 3.4646 12.543 2.242L12.542 2.242C12.293 2.1616 12.023 2.2912 11.938 2.5299C11.897 2.6463 11.905 2.7723 11.961 2.8826C12.018 2.9918 12.116 3.0758 12.237 3.1153C15.611 4.213 18.043 7.2841 18.043 10.9095C18.043 15.4575 14.224 19.1342 9.501 19.1342C4.777 19.1342 0.956 15.4572 0.956 10.9095C0.956 6.7107 4.214 3.2569 8.431 2.7508L7.355 4.0176C7.275 4.1124 7.237 4.2348 7.249 4.3559C7.263 4.4771 7.325 4.5899 7.424 4.6667C7.522 4.7434 7.649 4.7806 7.775 4.7674C7.901 4.7554 8.018 4.6954 8.098 4.6007L9.868 2.5145C9.878 2.5013 9.887 2.4881 9.895 2.4737C9.905 2.4605 9.913 2.4461 9.922 2.4317C9.951 2.3741 9.968 2.3117 9.971 2.2481C9.974 2.2397 9.977 2.2313 9.978 2.2218C9.977 2.2158 9.976 2.2086 9.973 2.2014C9.969 2.1378 9.952 2.0754 9.921 2.0178C9.914 2.0034 9.907 1.9902 9.898 1.977C9.872 1.9362 9.838 1.8978 9.8 1.8667L7.633 0.1619C7.43 0 7.128 0.0288 6.96 0.2243L6.96 0.2255C6.793 0.4223 6.824 0.7126 7.029 0.8733L8.26 1.8426C3.605 2.4305 0.001 6.2695 0 10.9096Z"
      fill="currentColor"
    />
    <path
      d="M7.456 7.8503V14.6639H6.638V8.7153H6.599L4.95 9.8198V8.9814L6.638 7.8503H7.456Z"
      fill="currentColor"
    />
    <path
      d="M11.602 14.7571C11.105 14.7571 10.682 14.6207 10.332 14.3479C9.983 14.0728 9.715 13.6747 9.531 13.1535C9.346 12.63 9.254 11.9979 9.254 11.2571C9.254 10.5207 9.346 9.8919 9.531 9.3707C9.718 8.8472 9.986 8.448 10.335 8.173C10.687 7.8957 11.109 7.7571 11.602 7.7571C12.094 7.7571 12.515 7.8957 12.865 8.173C13.217 8.448 13.485 8.8472 13.67 9.3707C13.857 9.8919 13.95 10.5207 13.95 11.2571C13.95 11.9979 13.858 12.63 13.673 13.1535C13.488 13.6747 13.221 14.0728 12.872 14.3479C12.522 14.6207 12.099 14.7571 11.602 14.7571ZM11.602 14.0252C12.094 14.0252 12.477 13.7856 12.75 13.3065C13.022 12.8274 13.158 12.1443 13.158 11.2571C13.158 10.6671 13.096 10.1647 12.97 9.75C12.847 9.3352 12.669 9.0191 12.436 8.8018C12.205 8.5844 11.927 8.4757 11.602 8.4757C11.114 8.4757 10.732 8.7186 10.457 9.2043C10.183 9.6879 10.045 10.3721 10.045 11.2571C10.045 11.8471 10.107 12.3484 10.23 12.7609C10.353 13.1734 10.53 13.4873 10.761 13.7024C10.994 13.9176 11.274 14.0252 11.602 14.0252Z"
      fill="currentColor"
    />
  </svg>
);

export const Plus10sIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="plus-10s"
    component={Plus10sSvg}
    onClick={onClick}
    className={anticonClass('plus-10s', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const Minus10sSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 19 20.0569" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19 10.9096C19 15.9567 14.741 20.0569 9.499 20.0569C4.258 20.0569 0 15.9554 0 10.9096C0 6.8871 2.701 3.4646 6.457 2.242L6.458 2.242C6.707 2.1616 6.977 2.2912 7.062 2.5299C7.103 2.6463 7.095 2.7723 7.039 2.8826C6.982 2.9918 6.884 3.0758 6.763 3.1153C3.389 4.213 0.957 7.2841 0.957 10.9095C0.957 15.4575 4.776 19.1342 9.499 19.1342C14.223 19.1342 18.044 15.4572 18.044 10.9095C18.044 6.7107 14.786 3.2569 10.569 2.7508L11.645 4.0176C11.725 4.1124 11.763 4.2348 11.751 4.3559C11.737 4.4771 11.675 4.5899 11.576 4.6667C11.478 4.7434 11.351 4.7806 11.225 4.7674C11.099 4.7554 10.982 4.6954 10.902 4.6007L9.132 2.5145C9.122 2.5013 9.113 2.4881 9.105 2.4737C9.095 2.4605 9.087 2.4461 9.078 2.4317C9.049 2.3741 9.032 2.3117 9.029 2.2481C9.026 2.2397 9.023 2.2313 9.022 2.2218C9.023 2.2158 9.024 2.2086 9.027 2.2014C9.031 2.1378 9.048 2.0754 9.079 2.0178C9.086 2.0034 9.093 1.9902 9.102 1.977C9.128 1.9362 9.162 1.8978 9.2 1.8667L11.367 0.1619C11.57 0 11.872 0.0288 12.04 0.2243L12.04 0.2255C12.207 0.4223 12.176 0.7126 11.971 0.8733L10.74 1.8426C15.395 2.4305 18.999 6.2695 19 10.9096Z"
      fill="currentColor"
    />
    <path
      d="M7.456 7.8503V14.6639H6.638V8.7153H6.599L4.95 9.8198V8.9814L6.638 7.8503H7.456Z"
      fill="currentColor"
    />
    <path
      d="M11.602 14.7571C11.105 14.7571 10.682 14.6207 10.332 14.3479C9.983 14.0728 9.715 13.6747 9.531 13.1535C9.346 12.63 9.254 11.9979 9.254 11.2571C9.254 10.5207 9.346 9.8919 9.531 9.3707C9.718 8.8472 9.986 8.448 10.335 8.173C10.687 7.8957 11.109 7.7571 11.602 7.7571C12.094 7.7571 12.515 7.8957 12.865 8.173C13.217 8.448 13.485 8.8472 13.67 9.3707C13.857 9.8919 13.95 10.5207 13.95 11.2571C13.95 11.9979 13.858 12.63 13.673 13.1535C13.488 13.6747 13.221 14.0728 12.872 14.3479C12.522 14.6207 12.099 14.7571 11.602 14.7571ZM11.602 14.0252C12.094 14.0252 12.477 13.7856 12.75 13.3065C13.022 12.8274 13.158 12.1443 13.158 11.2571C13.158 10.6671 13.096 10.1647 12.97 9.75C12.847 9.3352 12.669 9.0191 12.436 8.8018C12.205 8.5844 11.927 8.4757 11.602 8.4757C11.114 8.4757 10.732 8.7186 10.457 9.2043C10.183 9.6879 10.045 10.3721 10.045 11.2571C10.045 11.8471 10.107 12.3484 10.23 12.7609C10.353 13.1734 10.53 13.4873 10.761 13.7024C10.994 13.9176 11.274 14.0252 11.602 14.0252Z"
      fill="currentColor"
    />
  </svg>
);

export const Minus10sIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="minus-10s"
    component={Minus10sSvg}
    onClick={onClick}
    className={anticonClass('minus-10s', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const WarningSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.6047 17.2211" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.324 8.1918C10.5641 8.1918 10.7593 8.3864 10.7595 8.6264V13.7572C10.7593 13.9973 10.5641 14.1918 10.324 14.1918C10.0843 14.1914 9.8897 13.997 9.8894 13.7572V8.6264C9.8897 8.3867 10.0843 8.1923 10.324 8.1918Z"
      fill="currentColor"
    />
    <path
      d="M10.2898 5.6918C10.704 5.6918 11.0398 6.0276 11.0398 6.4418C11.0398 6.856 10.704 7.1918 10.2898 7.1918C9.8756 7.1918 9.5398 6.856 9.5398 6.4418C9.5399 6.0276 9.8756 5.6918 10.2898 5.6918Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.4324 0.6459C9.821 0 10.7586 0.0001 11.1472 0.6459L20.2039 15.7055C20.6047 16.372 20.1242 17.2211 19.3465 17.2211H1.2332L1.0906 17.2113C0.3986 17.1156 0 16.3303 0.3758 15.7055L9.4324 0.6459ZM10.4011 1.0942C10.3505 1.0108 10.2291 1.0107 10.1785 1.0942L1.1218 16.1537C1.0698 16.2404 1.1321 16.351 1.2332 16.351H19.3465C19.4476 16.351 19.5099 16.2404 19.4578 16.1537L10.4011 1.0942Z"
      fill="currentColor"
    />
  </svg>
);

export const WarningIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="warning"
    component={WarningSvg}
    onClick={onClick}
    className={anticonClass('warning', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const SortAZSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.0424 20.0325" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.9284 7.3563H14.0424L16.6064 0H17.4784L20.0424 7.3563H19.1564L17.0694 1.1638H17.0154L14.9284 7.3563ZM15.2564 4.4828H18.8284V5.273H15.2564V4.4828Z"
      fill="currentColor"
    />
    <path
      d="M14.6544 20V19.3534L18.4184 13.4339H14.6134V12.6437H19.4274V13.2902L15.6634 19.2098H19.4684V20H14.6544Z"
      fill="currentColor"
    />
    <path
      d="M0.1692 15.3036C0 15.1339 0.0002 14.8591 0.1692 14.6894C0.339 14.5196 0.6145 14.5189 0.7845 14.6884L4.6126 18.5155L4.6126 0.661C4.6128 0.4211 4.8073 0.2267 5.0472 0.2265C5.2871 0.2266 5.4825 0.4211 5.4827 0.661L5.4827 18.5644L9.844 14.203C10.0137 14.0339 10.2885 14.034 10.4583 14.203C10.628 14.3728 10.6286 14.6484 10.4593 14.8183L5.4046 19.873C5.2757 20.0017 5.0862 20.0325 4.928 19.9657C4.8508 19.9483 4.7773 19.9106 4.7171 19.8505L0.1692 15.3036Z"
      fill="currentColor"
    />
  </svg>
);

export const SortAZIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="sort-a-z"
    component={SortAZSvg}
    onClick={onClick}
    className={anticonClass('sort-a-z', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const SortZASvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.041 20.0109" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.927 7.3564H14.041L16.605 0H17.477L20.041 7.3564H19.155L17.068 1.1638H17.014L14.927 7.3564ZM15.255 4.4828H18.827V5.273H15.255V4.4828Z"
      fill="currentColor"
    />
    <path
      d="M14.653 20V19.3535L18.417 13.4339H14.612V12.6437H19.426V13.2903L15.662 19.2098H19.467V20H14.653Z"
      fill="currentColor"
    />
    <path
      d="M10.46 4.9337C10.629 5.1034 10.629 5.3782 10.46 5.548C10.29 5.7177 10.015 5.7184 9.845 5.549L6.016 1.7218L6.016 19.5763C6.016 19.8162 5.822 20.0106 5.582 20.0109C5.342 20.0107 5.147 19.8163 5.146 19.5763L5.146 1.673L0.785 6.0343C0.615 6.2035 0.341 6.2033 0.171 6.0343C0.001 5.8646 0 5.589 0.17 5.4191L5.224 0.3644C5.353 0.2356 5.543 0.2048 5.701 0.2716C5.778 0.2891 5.852 0.3268 5.912 0.3869L10.46 4.9337Z"
      fill="currentColor"
    />
  </svg>
);

export const SortZAIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="sort-z-a"
    component={SortZASvg}
    onClick={onClick}
    className={anticonClass('sort-z-a', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const GroupSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 20.028 19.1172" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.593 10.2481C12.701 10.2481 13.798 10.466 14.821 10.8897C15.844 11.3135 16.775 11.9347 17.558 12.7178C18.341 13.501 18.962 14.4318 19.386 15.4551C19.81 16.4783 20.028 17.5751 20.028 18.6826C20.028 18.9229 19.834 19.1172 19.593 19.1172C19.353 19.1169 19.159 18.9227 19.159 18.6826C19.159 17.6894 18.963 16.7057 18.583 15.7881C18.202 14.8704 17.645 14.0354 16.943 13.333C16.241 12.6307 15.406 12.0735 14.488 11.6934C13.57 11.3134 12.586 11.1172 11.593 11.1172C10.6 11.1173 9.616 11.3134 8.699 11.6934C7.781 12.0735 6.946 12.6306 6.244 13.333C5.541 14.0355 4.984 14.8703 4.604 15.7881C4.224 16.7057 4.028 17.6894 4.028 18.6826C4.028 18.9229 3.834 19.1172 3.593 19.1172C3.353 19.1169 3.159 18.9227 3.159 18.6826C3.159 17.5751 3.377 16.4783 3.8 15.4551C4.224 14.4318 4.845 13.501 5.629 12.7178C6.412 11.9346 7.343 11.3135 8.366 10.8897C9.389 10.4659 10.486 10.2481 11.593 10.2481ZM11.611 0C14.208 0.0001 16.313 2.1053 16.313 4.7022C16.313 7.2991 14.208 9.4042 11.611 9.4043C9.014 9.4042 6.909 7.2991 6.909 4.7022C6.909 2.1053 9.014 0.0001 11.611 0ZM11.611 0.8701C9.495 0.8702 7.779 2.5857 7.779 4.7022C7.779 6.8186 9.494 8.5341 11.611 8.5342C13.727 8.5341 15.443 6.8186 15.443 4.7022C15.443 2.5857 13.727 0.8702 11.611 0.8701Z"
      fill="currentColor"
    />
    <path
      d="M6.743 9.8193C7.63 9.8193 8.508 9.9994 9.327 10.3496C9.457 10.4049 9.585 10.4641 9.71 10.5274C9.925 10.6357 10.01 10.8979 9.902 11.1123C9.793 11.3264 9.532 11.4128 9.318 11.3047C9.209 11.2497 9.098 11.1974 8.986 11.1494C8.274 10.8452 7.512 10.6885 6.743 10.6885C5.973 10.6885 5.211 10.8452 4.499 11.1494C3.788 11.4537 3.141 11.9004 2.595 12.4639C2.049 13.0275 1.615 13.6974 1.319 14.4356C1.023 15.1738 0.87 15.9658 0.869 16.7656C0.869 17.0059 0.675 17.2012 0.435 17.2012C0.195 17.201 0 17.0058 0 16.7656C0 15.8555 0.173 14.9539 0.511 14.1123C0.849 13.2705 1.345 12.5041 1.97 11.8584C2.595 11.2128 3.339 10.6998 4.158 10.3496C4.977 9.9994 5.855 9.8194 6.743 9.8193Z"
      fill="currentColor"
    />
    <path
      d="M6.666 1.5597C7.367 1.5423 8.058 1.7304 8.659 2.1017L8.296 2.7277C8.059 2.6546 7.77 2.58 7.414 2.5089C7.41 2.508 7.404 2.5077 7.4 2.507C7.167 2.4505 6.928 2.4239 6.688 2.4298C6.158 2.4431 5.639 2.6109 5.194 2.9171C4.748 3.2238 4.391 3.6581 4.169 4.171C3.947 4.6841 3.87 5.2533 3.947 5.8117C4.025 6.3701 4.255 6.8921 4.606 7.3165C4.956 7.7407 5.414 8.0501 5.922 8.213C6.43 8.3758 6.971 8.3874 7.485 8.2452C7.728 8.1777 7.961 8.0747 8.178 7.9435L8.514 7.5586L9.01 7.5333L9.481 7.9816C9.006 8.5132 8.394 8.8956 7.717 9.0831L7.461 9.1437C6.948 9.2464 6.419 9.2366 5.909 9.1134L5.656 9.0421C5.071 8.8544 4.541 8.5197 4.113 8.0704L3.936 7.8712C3.538 7.3896 3.262 6.8143 3.132 6.1984L3.086 5.9327C2.999 5.3064 3.063 4.6683 3.272 4.0763L3.37 3.8253C3.621 3.2473 4.005 2.743 4.489 2.3576L4.701 2.2003C5.285 1.7996 5.966 1.5772 6.666 1.5597Z"
      fill="currentColor"
    />
  </svg>
);

export const GroupIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="group"
    component={GroupSvg}
    onClick={onClick}
    className={anticonClass('group', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

const MoveCardSvg: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} viewBox="0 0 27 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter
        id="moveCardShadow"
        x="-20%"
        y="-10%"
        width="140%"
        height="130%"
        filterUnits="objectBoundingBox"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="4" />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
      </filter>
    </defs>
    <g filter="url(#moveCardShadow)">
      <rect x="0" y="0" width="27" height="34" rx="4" fill="white" />
      <circle cx="9" cy="9" r="2" fill="#D9D9D9" />
      <circle cx="18" cy="9" r="2" fill="#D9D9D9" />
      <circle cx="9" cy="17" r="2" fill="#D9D9D9" />
      <circle cx="18" cy="17" r="2" fill="#D9D9D9" />
      <circle cx="9" cy="25" r="2" fill="#D9D9D9" />
      <circle cx="18" cy="25" r="2" fill="#D9D9D9" />
    </g>
  </svg>
);

export const MoveCardIcon: FC<InteractiveIconProps> = ({
  size,
  color,
  style,
  onClick,
  className,
  ...rest
}) => (
  <Icon
    {...rest}
    aria-label="move-card"
    component={MoveCardSvg}
    onClick={onClick}
    className={anticonClass('move-card', className)}
    style={{
      ...(color ? { color } : {}),
      ...(size === undefined ? {} : { fontSize: size }),
      ...style,
    }}
  />
);

// ===========================================================================
// ICON MAP FOR DYNAMIC ACCESS
// ===========================================================================

export const ICONS = {
  gpt: GptIcon,
  menu: MenuIcon,
  sidebarToggle: SidebarToggleIcon,
  history: HistoryIcon,
  favorite: HeartIcon,
  favoriteFilled: HeartFilledIcon,
  web: WebIcon,
  upload: UploadIcon,
  context: ContextIcon,
  matters: MattersIcon,
  submit: SubmitIcon,
  sort: SortIcon,
  filter: FilterIcon,
  calendar: CalendarIcon,
  visibility: VisibilityIcon,
  reset: ResetIcon,
  reuse: ReuseIcon,
  inverse: InverseIcon,
  exit: ExitIcon,
  add: AddIcon,
  verify: VerifyIcon,
  tooltipInfo: TooltipInfoIcon,
  tooltipHelp: TooltipHelpIcon,
  arrowRight: ArrowRightIcon,
  arrowLeft: ArrowLeftIcon,
  clipboard: ClipboardIcon,
  tour: TourIcon,
  play: PlayIcon,
  pause: PauseIcon,
  stop: StopIcon,
  plus10s: Plus10sIcon,
  minus10s: Minus10sIcon,
  warning: WarningIcon,
  sortAZ: SortAZIcon,
  sortZA: SortZAIcon,
  group: GroupIcon,
  moveCard: MoveCardIcon,
  compose: ComposeIcon,
  save: SaveIcon,
  publish: PublishIcon,
  delete: DeleteIcon,
  copy: CopyIcon,
  duplicate: DuplicateIcon,
  move: MoveIcon,
  select: SelectIcon,
  edit: EditIcon,
  pin: PinIcon,
  pinFilled: PinFilledIcon,
  home: HomeIcon,
  folder: FolderIcon,
  search: SearchIcon,
  library: LibraryIcon,
  chat: ChatIcon,
  composer: ComposerIcon,
  quote: QuoteIcon,
  analysis: AnalysisIcon,
  template: TemplateIcon,
  review: ReviewIcon,
  sideBySide: SideBySideIcon,
  comparison: ComparisonIcon,
  userGuide: UserGuideIcon,
  team: TeamIcon,
  logout: LogoutIcon,
  platform: PlatformIcon,
  projectsHub: ProjectsHubIcon,
  scribe: ScribeIcon,
  workstudio: WorkstudioIcon,
  translation: TranslationIcon,
  setting: SettingIcon,
  diagnostics: DiagnosticsIcon,
  management: ManagementIcon,
  admin: AdminIcon,
  grammarOfUse: GrammarOfUseIcon,
  contact: ContactIcon,
  language: LanguageIcon,
  reportBug: ReportBugIcon,
  formWrite: FormWriteIcon,
} as const;
